import {
  AdjustmentsHorizontalIcon,
  BuildingOffice2Icon,
  ChevronRightIcon,
  FunnelIcon,
  RectangleStackIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarIcon,
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  SlashIcon,
  TriangleUpIcon,
} from "@radix-ui/react-icons";
import * as Slider from "@radix-ui/react-slider";
import "@tanstack/react-table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import dayjs from "dayjs";
import { forwardRef, memo, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CustomFieldData,
  CustomFieldMultiSelectData,
  CustomFieldSelectData,
  useGetCustomFields,
} from "src/api/CustomFields/custom-fields";
import {
  CustomerData,
  useGetCustomersWithDebouncing,
} from "src/api/General/customers";
import { SettingsData, useGetSettings } from "src/api/General/settings";
import {
  ProjectDataV2,
  ProjectDetailsData,
  ProjectsSort,
  ProjectsV2Filter,
  ProjectsV2FilterGroup,
  useGetProjectsDetails,
  useGetProjectsV2,
} from "src/api/Services/Projects/projects";
import {
  InternalPhaseUpdateData,
  PhaseStatus,
} from "src/api/Services/SharedSpace/phases";
import { useGetJourneyTemplates } from "src/api/Services/Tasks/journeys";
import {
  useGetProjectOwnersForSpace,
  useGetSpace,
  useGetSpaces,
} from "src/api/Spaces/spaces";
import { SpaceSymbol } from "src/components/Layout/Sidebar";
import { Button } from "src/components/ui/button";
import { Calendar } from "src/components/ui/calendar";
import { Checkbox } from "src/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import { Count } from "src/components/ui/count";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";
import { Loading } from "src/components/ui/loading";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "src/components/ui/select";
import { TextInput } from "src/components/ui/text-input";
import NewProjectButton from "src/pages/Services/Projects/NewProject/NewProject";
import {
  InternalUpdate,
  getPhaseStatus,
} from "src/pages/Services/SharedSpace/Phases/Tabs/Overview";
import useDebounce from "src/utils/customHooks/useDebounce";
import {
  CompanyFilter,
  CompanyFilterModel,
  IdComparator,
  Logic,
  ProjectActiveTimeFilterModel,
  ProjectOwnerFilter,
  ProjectOwnerFilterModel,
  ProjectProgressFilter,
  ProjectProgressFilterModel,
  ProjectStatusFilter,
  ProjectStatusFilterModel,
  ProjectTemplateFilter,
  ProjectTemplateFilterModel,
  SpaceFilter,
  SpaceFilterModel,
  StartDateFilter,
  StartDateFilterModel,
  getSpaceProjectsColumnsToDisplayKey,
  getSpaceProjectsFilterKey,
} from "src/utils/filters";
import { getFirstAndLastInitials } from "src/utils/functions/avatar";
import { getTimeDifference } from "src/utils/functions/dateTime";
import { SpaceNameAndSymbol } from "../spaces";

export default function SpaceProjectsWrapper() {
  const { spaceId } = useParams();
  return <SpaceProjects key={spaceId} />;
}

export type ColumnsToDisplay = {
  [key: string]: {
    id: string;
    visible: boolean;
    name: string;
    width: number;
    isCustomField: boolean;
  };
};

export function addSearchToFilterGroup(
  filterGroup: ProjectsV2FilterGroup,
  search: string
): ProjectsV2FilterGroup {
  if (!search) return filterGroup;
  return {
    ...filterGroup,
    children: [
      { field: "name", comparator: "contains", value: search },
      ...filterGroup.children,
    ],
  };
}

function SpaceProjects() {
  const { spaceId } = useParams();
  const { data: space } = useGetSpace(String(spaceId));
  const { data: spaces } = useGetSpaces();
  // const { data: spaceMemberships } = useGetSpaceMemberships(String(spaceId));
  const { data: customFields } = useGetCustomFields("project");
  const { data: projectOwners } = useGetProjectOwnersForSpace(String(spaceId));
  const [columnsToDisplay, setColumnsToDisplay] = useState<ColumnsToDisplay>(
    () => {
      return x();
    }
  );

  // const [actualPageNo, setActualPageNo] = useState(1);
  // const [displayedPageNo, setDisplayedPageNo] = useState(1);
  const [totalNoOfPages, setTotalNoOfPages] = useState<number | undefined>(
    undefined
  );

  function x(): ColumnsToDisplay {
    let initialColumnsToDisplay: ColumnsToDisplay = {
      company: {
        id: "company",
        visible: true,
        name: "Company",
        width: 208,
        isCustomField: false,
      },
      internalUpdates: {
        id: "internalUpdates",
        visible: true,
        name: "Updates",
        width: 208,
        isCustomField: false,
      },
      owner: {
        id: "owner",
        visible: true,
        name: "Owner",
        width: 208,
        isCustomField: false,
      },
      deltaToTarget: {
        id: "deltaToTarget",
        visible: true,
        name: "Target vs Forecast",
        width: 208,
        isCustomField: false,
      },
      daysActive: {
        id: "daysActive",
        visible: true,
        name: "Days Active",
        width: 140,
        isCustomField: false,
      },
      startDate: {
        id: "startDate",
        visible: true,
        name: "Start Date",
        width: 208,
        isCustomField: false,
      },
      endDate: {
        id: "endDate",
        visible: true,
        name: "End Date",
        width: 208,
        isCustomField: false,
      },
      forecastedCompletionDate: {
        id: "forecastedCompletionDate",
        visible: true,
        name: "Forecasted End Date",
        width: 208,
        isCustomField: false,
      },
      template: {
        id: "template",
        visible: true,
        name: "Template",
        width: 208,
        isCustomField: false,
      },
    };

    // add custom fields
    if (customFields) {
      for (const customField of customFields) {
        initialColumnsToDisplay[customField._id] = {
          id: customField._id,
          visible: false,
          name: customField.name,
          width: 160,
          isCustomField: true,
        };
      }
    }

    const savedColumnsToDisplay = localStorage.getItem(
      getSpaceProjectsColumnsToDisplayKey(String(spaceId))
    );
    if (savedColumnsToDisplay) {
      const parsedColumnsToDisplay = JSON.parse(savedColumnsToDisplay);
      // set visbility based on saved state
      for (const key of Object.keys(initialColumnsToDisplay)) {
        if (parsedColumnsToDisplay.hasOwnProperty(key)) {
          // console.log(savedColumnsToDisplay[column.id])
          initialColumnsToDisplay[key] = {
            ...initialColumnsToDisplay[key],
            visible: !!parsedColumnsToDisplay[key].visible,
          };
        }
      }
    }
    // console.log(initialColumnsToDisplay);
    return initialColumnsToDisplay;
  }

  useEffect(() => {
    if (customFields) {
      setColumnsToDisplay(x());
    }
  }, [customFields]);

  const [filterGroup, setFilterGroup] = useState<ProjectsV2FilterGroup>(() => {
    const savedFilterGroup = localStorage.getItem(
      getSpaceProjectsFilterKey(String(spaceId))
    );
    if (savedFilterGroup) {
      return JSON.parse(savedFilterGroup);
    }
    return {
      logic: "AND",
      children: [
        {
          field: "project-status",
          comparator: "in",
          value: ["In Progress"],
        },
      ],
    };
  });

  const [sort, setSort] = useState<ProjectsSort>({
    property: "createdAt",
    order: "desc",
  });

  const [pageNo, setPageNo] = useState(1);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setPageNo(1);
  }, [filterGroup, search]);

  const { data: projects, isLoading } = useGetProjectsV2(
    {
      logic: "AND",
      children: [
        {
          field: "space",
          comparator: "in",
          value: [String(spaceId)],
        },
      ],
    },
    // addSearchToFilterGroup(clean({
    //   logic: "AND",
    //   children: [
    //     {
    //       field: "project-start-date",
    //       comparator: "gte",
    //       value: dayjs().subtract(30, "days").toISOString(),
    //     },
    //     {
    //       field: "project-start-date",
    //       comparator: "lte",
    //       value: dayjs().toISOString(),
    //     },
    //   ],
    // }), search),
    addSearchToFilterGroup(clean(filterGroup), search),
    pageNo,
    sort
  );
  // console.log(projects);

  // useEffect(() => {
  //   setPageNo(1);
  // }, [filterGroup]);

  const { data: projectDetails } = useGetProjectsDetails(
    {
      logic: "AND",
      children: [
        {
          field: "space",
          comparator: "in",
          value: [String(spaceId)],
        },
      ],
    },
    addSearchToFilterGroup(clean(filterGroup), search),
    pageNo,
    sort
  );
  // console.log(projectDetails);
  // const [noOfProjectsAfterSearch, setNoOfProjectsAfterSearch] = useState(0);
  const { journeyTemplates } = useGetJourneyTemplates();

  // const [debouncedSearch, setDebouncedSearch] = useState(search);
  // useEffect(() => {
  //   const timeout = setTimeout(() => {
  //     setDebouncedSearch(search);
  //   }, 300);

  //   return () => clearTimeout(timeout);
  // }, [search]);

  useEffect(() => {
    if (projects?.pagination.totalPages === 0) {
      setTotalNoOfPages(1);
    } else if (projects?.pagination.totalPages) {
      setTotalNoOfPages(projects?.pagination.totalPages);
    }
  }, [projects?.pagination.totalPages]);

  useEffect(() => {
    if (filterGroup.children.length === 0) {
      localStorage.removeItem(getSpaceProjectsFilterKey(String(spaceId)));
    } else {
      localStorage.setItem(
        getSpaceProjectsFilterKey(String(spaceId)),
        JSON.stringify(filterGroup)
      );
    }
  }, [filterGroup]);

  useEffect(() => {
    const savedColumnsToDisplay = localStorage.getItem(
      getSpaceProjectsColumnsToDisplayKey(String(spaceId))
    );

    if (savedColumnsToDisplay) {
      let parsedColumnsToDisplay = JSON.parse(savedColumnsToDisplay);
      // set visbility of localstorage based on columnsToDisplay
      for (const key of Object.keys(parsedColumnsToDisplay)) {
        if (columnsToDisplay.hasOwnProperty(key)) {
          parsedColumnsToDisplay[key].visible = columnsToDisplay[key].visible;
        }
      }
      // add new columns to localstorage
      for (const key of Object.keys(columnsToDisplay)) {
        if (!parsedColumnsToDisplay.hasOwnProperty(key)) {
          parsedColumnsToDisplay[key] = columnsToDisplay[key];
        }
      }
      localStorage.setItem(
        getSpaceProjectsColumnsToDisplayKey(String(spaceId)),
        JSON.stringify(parsedColumnsToDisplay)
      );
    } else {
      localStorage.setItem(
        getSpaceProjectsColumnsToDisplayKey(String(spaceId)),
        JSON.stringify(columnsToDisplay)
      );
    }
  }, [columnsToDisplay]);

  // debounce search
  // const debouncedSearch = useDebounce(search, 300);

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex h-12 items-center border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2 text-sm">
          <div className="flex cursor-default items-center space-x-1.5 text-gray-800">
            {space && <SpaceSymbol color={space?.color} />}
            <span>{space?.name}</span>
          </div>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Projects"}</div>
        </div>
      </div>

      <div className="flex h-14 items-center justify-between border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2">
          <Filter
            filterGroup={filterGroup}
            setFilterGroup={setFilterGroup}
            isLoading={!projectOwners}
            // localStorageKey="project-filter"
            filterModels={[
              {
                field: "project-owner",
                projectOwners:
                  // Array.from(
                  //   new Map(
                  //     projects?.projects.map((p) => [
                  //       p.owner._id,
                  //       {
                  //         _id: p.owner._id,
                  //         name: p.owner.name,
                  //         email: p.owner.email,
                  //       },
                  //     ])
                  //   ).values()
                  // ) ?? [],
                  projectOwners?.map((m) => {
                    return {
                      _id: m._id,
                      name: m.name,
                      email: m.email,
                    };
                  }) ?? [],
              },
              { field: "project-status" },
              { field: "company" },
              {
                field: "project-template",
                projectTemplates:
                  journeyTemplates
                    ?.filter((jt) => jt.spaceId?.toString() === spaceId)
                    .map((jt) => {
                      return {
                        _id: jt._id?.toString() ?? "",
                        name: jt.name,
                      };
                    }) ?? [],
              },
              { field: "project-start-date" },
            ]}
          />
          <StatusFilter
            filterGroup={filterGroup}
            setFilterGroup={setFilterGroup}
          />
          <Display
            columnsToDisplay={columnsToDisplay}
            setColumnsToDisplay={setColumnsToDisplay}
          />
          <div className="relative">
            <SearchInput
              onChange={(value) => {
                setSearch(value);
              }}
              initialValue={search}
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-2.5">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
          </div>
          <div
            className={clsx(
              "flex h-8 items-center space-x-2 overflow-hidden rounded-md border border-input shadow-sm"
              // isLoading && "opacity-50"
            )}
          >
            <div
              className={clsx(
                "flex h-full w-7 items-center justify-center border-r border-input transition-all",
                pageNo === 1
                  ? "cursor-not-allowed text-gray-300"
                  : "cursor-pointer text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              )}
              onClick={() => {
                if (pageNo === 1) return;
                setPageNo((prev) => prev - 1);
              }}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </div>
            <div className="flex select-none items-center space-x-1 px-1 text-sm text-gray-600">
              <div className="min-w-[12px] text-center">{pageNo}</div>
              <SlashIcon className="h-3 w-3 text-gray-500" />
              <div className="min-w-[12px] text-center">
                {totalNoOfPages ? (
                  totalNoOfPages
                ) : (
                  <div className="h-3 w-3 animate-pulse rounded bg-gray-200" />
                )}
              </div>
            </div>
            <div
              className={clsx(
                "flex h-full w-7 items-center justify-center border-l border-input transition-all",
                pageNo === totalNoOfPages
                  ? "cursor-not-allowed text-gray-300"
                  : "cursor-pointer text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              )}
              onClick={() => {
                if (pageNo === totalNoOfPages) return;
                setPageNo((prev) => prev + 1);
              }}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </div>
          </div>
          {/* {!!projects && (
            <div className="text-sm text-gray-600 h-8 rounded-md px-2 flex items-center">
              {`Showing ${noOfProjectsAfterSearch} of ${projects.noOfProjects} projects`}
            </div>
          )} */}
          {/* <Button variant={"secondary"}>Display</Button> */}
        </div>
        {spaces && <NewProjectButton spaces={spaces} />}
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto bg-gray-50/70">
        {!!projects && !!spaces ? (
          <>
            {projects.pagination.baselineCount > 0 ? (
              <>
                <GeneralProjectsList
                  projects={projects.projects}
                  projectDetails={projectDetails?.projects}
                  columnsToDisplay={columnsToDisplay}
                  setColumnsToDisplay={setColumnsToDisplay}
                  // filterGroup={filterGroup}
                  // search={search}
                  // setSearch={setSearch}
                  sort={sort}
                  setSort={setSort}
                  // setNoOfProjectsAfterSearch={setNoOfProjectsAfterSearch}
                />
                {/* <div className="flex h-12 items-center justify-center border-y border-gray-200/70">
                  
                </div> */}
              </>
            ) : (
              <div className="mx-auto mt-[10vh]">
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-t from-gray-200 to-transparent px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
                <div className="flex">
                  <div className="w-20 bg-gradient-to-l from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="w-[420px] border bg-gray-50/70 p-12">
                    <div className="text-xl font-medium">Projects</div>
                    <div className="pt-2.5 font-light text-gray-700">
                      Projects are used to group together tasks that help
                      achieve a clear goal.
                    </div>
                    <div className="pt-6">
                      <NewProjectButton spaces={spaces} />
                    </div>
                  </div>
                  <div className="w-20 bg-gradient-to-r from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                </div>
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-b from-gray-200 to-white px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
              </div>
            )}
          </>
        ) : (
          <Loading className="mt-[20vh]" />
        )}
      </div>
    </div>
  );
}

export const SearchInput = memo(
  ({
    onChange,
    initialValue,
  }: {
    onChange: (value: string) => void;
    initialValue: string;
  }) => {
    const [value, setValue] = useState(initialValue);
    useEffect(() => {
      const timeout = setTimeout(() => {
        onChange(value);
      }, 500);
      return () => clearTimeout(timeout);
    }, [value]);

    return (
      <TextInput
        className="h-8 w-72 pl-8"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search.."
        autoFocus
      />
    );
  }
);

const Display = memo(
  ({
    columnsToDisplay,
    setColumnsToDisplay,
  }: {
    columnsToDisplay: ColumnsToDisplay;
    setColumnsToDisplay: (value: ColumnsToDisplay) => void;
  }) => {
    const { data: customFields } = useGetCustomFields("project");
    // console.log(columnsToDisplay);
    return (
      <Popover modal={true}>
        <PopoverTrigger asChild>
          <Button variant="secondary">
            <AdjustmentsHorizontalIcon className="mr-2 h-3.5 w-3.5 border-gray-300" />
            Display
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-[60vh] w-60 space-y-0 overflow-y-auto p-2"
        >
          {Object.keys(columnsToDisplay).map((key) => {
            const column = columnsToDisplay[key];
            return (
              <div
                className="flex cursor-pointer items-center justify-between gap-6 rounded-md px-2 py-1.5 hover:bg-gray-100"
                onClick={() => {
                  setColumnsToDisplay({
                    ...columnsToDisplay,
                    [key]: {
                      ...column,
                      visible: !column.visible,
                    },
                  });
                }}
              >
                <div className="truncate text-sm">{column.name}</div>
                <div>
                  <Checkbox
                    checked={column.visible}
                    // onCheckedChange={(checked) => {
                    //   setColumnsToDisplay({
                    //     ...columnsToDisplay,
                    //     [key]: {
                    //       ...column,
                    //       visible: !!checked,
                    //     },
                    //   });
                    // }}
                  />
                </div>
              </div>
            );
          })}
          {/* <div className="flex items-center justify-between">
          <div>Company</div>
          <div>
            <Checkbox
              checked={columnsToDisplay["company"].visible}
              onCheckedChange={(checked) => {
                setColumnsToDisplay({
                  ...columnsToDisplay,
                  company: {
                    ...columnsToDisplay["company"],
                    visible: !!checked,
                  },
                });
              }}
            />
          </div>
        </div> */}
        </PopoverContent>
      </Popover>
    );
  }
);

export function clean(filterGroup: ProjectsV2FilterGroup) {
  return {
    logic: filterGroup.logic,
    children: filterGroup.children.filter((child) => {
      if (child.field === "project-owner") {
        return !!child.value.length;
      } else if (child.field === "project-status") {
        return !!child.value.length;
      } else if (child.field === "company") {
        return !!child.value.length;
      } else if (child.field === "project-template") {
        return !!child.value.length;
      } else if (!child.field) {
        return false;
      } else if (child.field === "space") {
        return !!child.value.length;
      } else if (child.field === "project-start-date") {
        return !!child.value;
      } else if (child.field === "project-progress") {
        return true;
      } else if (child.field === "project-active-time") {
        return true;
      }
      return true;
    }),
  };
}

export const StatusFilter = memo(
  ({
    filterGroup,
    setFilterGroup,
  }: {
    filterGroup: ProjectsV2FilterGroup;
    setFilterGroup: (filterGroup: ProjectsV2FilterGroup) => void;
  }) => {
    const statusFilter = filterGroup.children.find(
      (f) => f.field === "project-status"
    ) as ProjectStatusFilter;
    const statusFilterIndex = filterGroup.children.findIndex(
      (f) => f.field === "project-status"
    );

    useEffect(() => {
      const doesStatusFilterExist = filterGroup.children.find(
        (f) => f.field === "project-status"
      );
      if (!doesStatusFilterExist) {
        setFilterGroup({
          logic: "AND",
          children: [
            ...filterGroup.children,
            {
              field: "project-status",
              comparator: "in",
              value: ["In Progress"],
            },
          ],
        });
      }
    }, [filterGroup]);

    if (!statusFilter || statusFilterIndex === -1) {
      return null;
    }

    return (
      <StatusCombobox
        filter={statusFilter}
        index={statusFilterIndex}
        filterGroup={filterGroup}
        setFilterGroup={setFilterGroup}
      />
    );
  }
);

function getPhaseStatusSymbol(
  status: PhaseStatus,
  isClickable: boolean = false
) {
  switch (status) {
    // case "Paused":
    //   return (
    //     <div className="flex items-center space-x-2">
    //       {/* <div className={clsx("group rounded-md", isClickable && "p-0")}> */}
    //       <div
    //         className={clsx(
    //           "h-4 w-4 rounded-full border-[2px] border-amber-400 transition-all",
    //           isClickable && "hover:border-amber-500"
    //         )}
    //       />
    //       {/* </div> */}
    //       {/* <div className="h-3.5 w-3.5 rounded border border-amber-600 bg-amber-400" />
    //       <div className="h-3.5 w-3.5 rounded-full border border-amber-600 bg-amber-400" />
    //       <div className="h-3.5 w-3.5 rounded-full border-[1.5px] border-amber-400 bg-amber-100" /> */}
    //     </div>
    //   );
    case "Paused":
      return (
        <div
          className={clsx(
            "h-4 w-4 rounded-full border-[2px] border-dashed border-gray-400 transition-all",
            isClickable && "hover:border-gray-500"
          )}
        />
      );

    case "Canceled":
      return (
        <div
          className={clsx(
            "flex h-4 w-4 items-center justify-center rounded-full bg-gray-500/60 text-white transition-all",
            isClickable && "hover:bg-gray-500/80"
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={3.5}
            stroke="currentColor"
            className="h-2.5 w-2.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </div>
      );

    case "In Progress":
      return (
        <div
          className={clsx(
            "group flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] border-blue-500 transition-all",
            isClickable && "hover:border-blue-600"
          )}
        >
          <div
            className={clsx(
              "h-2 w-2 rounded-full bg-blue-400",
              isClickable && "transition-all group-hover:bg-blue-500"
            )}
          />
        </div>
      );
    // case "Stuck":
    //   return (
    //     <div
    //       className={clsx(
    //         "group flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] border-red-500 transition-all",
    //         isClickable && "hover:border-red-600"
    //       )}
    //     >
    //       <div
    //         className={clsx(
    //           "h-2 w-2 rounded-full bg-red-400",
    //           isClickable && "group-hover:bg-red-500 transition-all"
    //         )}
    //       />
    //     </div>
    //   );
    case "Completed":
      return (
        <div className="flex items-center space-x-2">
          <div
            className={clsx(
              "flex h-4 w-4 items-center justify-center rounded-full bg-green-500 text-white transition-all",
              isClickable && "hover:bg-green-600"
            )}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={3}
              stroke="currentColor"
              className="h-3 w-3"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m4.5 12.75 6 6 9-13.5"
              />
            </svg>
          </div>
        </div>
      );
    default:
      return <div>TBD</div>;
  }
}

function StatusCombobox({
  filter,
  index,
  filterGroup,
  setFilterGroup,
}: {
  filter: ProjectStatusFilter;
  index: number;
  filterGroup: ProjectsV2FilterGroup;
  setFilterGroup: (filterGroup: ProjectsV2FilterGroup) => void;
}) {
  const [valueOpen, setValueOpen] = useState(!filter.value?.length);

  const handleValueChange = (value: string) => {
    setFilterGroup({
      ...filterGroup,
      // children: filterGroup.children.map((child, i) => {
      //   if (i === index) {
      //     return {
      //       ...child,
      //       value: child.value.includes(value)
      //         ? [
      //             ...(child as ProjectStatusFilter).value.filter(
      //               (v) => v !== value
      //             ),
      //           ]
      //         : [...(child as ProjectStatusFilter).value, value],
      //     } as ProjectStatusFilter;
      //   }
      //   return child;
      // }),
      children: [
        ...filterGroup.children.filter(
          (child) => child.field !== "project-status"
        ),
        {
          field: "project-status",
          comparator: "in",
          value: filter.value.includes(value)
            ? filter.value.filter((v) => v !== value)
            : [...filter.value, value],
        },
      ],
    });
  };

  function getStatusDisplay() {
    if (filter.value.length === 1) {
      return (
        <div className="flex items-center space-x-2">
          {getPhaseStatusSymbol(filter.value[0] as PhaseStatus, false)}
          <div className="text-sm text-gray-600">{filter.value[0]}</div>
        </div>
      );
    } else {
      return (
        <div className="flex items-center space-x-2">
          {filter.value.map((status) =>
            getPhaseStatusSymbol(status as PhaseStatus, false)
          )}
        </div>
      );
    }
  }

  return (
    <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary" className="py-0">
          <div className="flex h-full items-center border-r border-input pr-2.5">
            Status
          </div>
          <div className="pl-2.5">{getStatusDisplay()}</div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="pointer-events-auto w-60 p-0" align="start">
        <Command className="relative">
          <CommandList>
            <CommandGroup>
              {statuses.map((status) => (
                <CommandItem
                  key={status}
                  value={status}
                  onSelect={() => {
                    if (
                      filter.value.length === 1 &&
                      filter.value[0] === status
                    ) {
                      return;
                    }
                    handleValueChange(status);
                  }}
                  className={clsx(
                    "gap-2",
                    filter.value.length === 1 && filter.value[0] === status
                      ? "cursor-not-allowed"
                      : ""
                  )}
                >
                  <Checkbox
                    checked={filter.value.includes(status)}
                    disabled={
                      filter.value.length === 1 && filter.value[0] === status
                    }
                  />
                  <div className="flex items-center space-x-2">
                    <div>{getPhaseStatusSymbol(status)}</div>
                    <span className="truncate">{status}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type FilterModel =
  | ProjectStatusFilterModel
  | SpaceFilterModel
  | ProjectOwnerFilterModel
  | CompanyFilterModel
  | ProjectTemplateFilterModel
  | StartDateFilterModel
  | ProjectProgressFilterModel
  | ProjectActiveTimeFilterModel;

const FIELD_COMPARATOR_WIDTH = "w-32";
const FIELD_VALUE_WIDTH = "w-52";

export const Filter = memo(
  ({
    filterModels,
    filterGroup,
    setFilterGroup,
    isLoading,
  }: {
    filterModels: FilterModel[];
    filterGroup: ProjectsV2FilterGroup;
    setFilterGroup: (filterGroup: ProjectsV2FilterGroup) => void;
    isLoading: boolean;
  }) => {
    const [open, setOpen] = useState(false);
    const [filterCount, setFilterCount] = useState(0);
    const { spaceId } = useParams();

    useEffect(() => {
      // count the number of valid filters
      let count = 0;
      let hasStartDateFilter = false;
      let hasProgressFilter = false;
      for (let i = 0; i < filterGroup.children.length; i++) {
        const child = filterGroup.children[i] as ProjectsV2Filter;
        if (child.field === "project-owner") {
          if (child.value.length > 0) {
            count++;
          }
        } else if (child.field === "company") {
          if (child.value.length > 0) {
            count++;
          }
        } else if (child.field === "project-template") {
          if (child.value.length > 0) {
            count++;
          }
        } else if (child.field === "space") {
          if (child.value.length > 0) {
            count++;
          }
        } else if (child.field === "project-start-date") {
          if (!!child.value) {
            hasStartDateFilter = true;
          }
        } else if (child.field === "project-progress") {
          hasProgressFilter = true;
        }
      }
      count += hasStartDateFilter ? 1 : 0;
      count += hasProgressFilter ? 1 : 0;
      setFilterCount(count);
    }, [filterGroup]);

    useEffect(() => {
      if (!open) {
        setFilterGroup(clean(filterGroup));
      }
    }, [open]);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="secondary" disabled={isLoading}>
            <FunnelIcon className="mr-2 h-3.5 w-3.5 border-gray-300" />
            Filter
            {filterCount > 0 && (
              <div className="ml-2">
                <Count variant={"primaryLight"}>{filterCount}</Count>
              </div>
            )}
            {filterCount > 0 && (
              <button
                tabIndex={-1}
                className="ml-1.5 shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  setFilterGroup({
                    logic: "AND",
                    children: filterGroup.children.filter(
                      (child) => child.field === "project-status"
                    ),
                  });
                }}
              >
                <XMarkIcon className="h-2.5 w-2.5 " />
              </button>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-40 w-full min-w-[480px] space-y-3 p-5"
        >
          <FilterGroup
            filterGroup={filterGroup}
            setFilterGroup={setFilterGroup}
            level={0}
            filterModels={filterModels}
          />
        </PopoverContent>
      </Popover>
    );
  }
);

function NoProjectsMeetFilter() {
  return (
    <div className="mx-auto mt-[10vh]">
      No projects meet the filter criteria
    </div>
  );
}

function FilterGroup({
  filterGroup,
  setFilterGroup,
  level,
  filterModels,
}: // phase,
{
  filterGroup: ProjectsV2FilterGroup;
  setFilterGroup: (filterGroup: ProjectsV2FilterGroup) => void;
  level: number;
  filterModels: FilterModel[];
}) {
  const handleLogicChange = (logic: Logic) => {
    setFilterGroup({ ...filterGroup, logic });
  };

  const addFilter = (field: ProjectsV2Filter["field"]) => {
    if (field === "project-owner") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          {
            field: field,
            comparator: "in",
            value: [],
          },
        ],
      });
    } else if (field === "company") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          {
            field: field,
            comparator: "in",
            value: [],
          },
        ],
      });
    } else if (field === "project-template") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          {
            field: field,
            comparator: "in",
            value: [],
          },
        ],
      });
    } else if (field === "space") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          { field, comparator: "in", value: [] },
        ],
      });
    } else if (field === "project-start-date") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          { field, comparator: "gte", value: "" },
        ],
      });
    } else if (field === "project-progress") {
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children,
          { field, comparator: "gte", value: 0 },
          { field, comparator: "lte", value: 100 },
        ],
      });
    }
  };

  const [open, setOpen] = useState(false);

  const startDateFilters = useMemo(() => {
    return filterGroup.children.filter(
      (child) => child.field === "project-start-date"
    ) as StartDateFilter[];
  }, [filterGroup]);

  const progressFilters = useMemo(() => {
    return filterGroup.children.filter(
      (child) => child.field === "project-progress"
    ) as ProjectProgressFilter[];
  }, [filterGroup]);

  return (
    <div className={clsx(level > 0 && "rounded-md border p-4")}>
      <div>
        <div className="flex items-start space-x-3">
          <div className="space-y-0.5">
            <div className="inline-flex h-8 w-[52px] items-center justify-center text-sm text-gray-500">
              Where
            </div>
            {/* {filterGroup.children.length > 2 && (
              <Select
                value={filterGroup.logic}
                onValueChange={(value) => {
                  handleLogicChange(value as Logic);
                }}
              >
                <SelectTrigger
                  className="w-[52px] justify-center bg-white"
                  showIcon={false}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="AND">AND</SelectItem>
                    <SelectItem value="OR">OR</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            )} */}
            {/* show uneditable and/or for each of the filters */}
            {Array.from({
              length:
                new Set(filterGroup.children.map((f) => f.field)).size - 2,
            }).map((_, index) => (
              <div
                key={index}
                className="flex h-8 w-[52px] items-center justify-center text-sm text-gray-400"
              >
                {filterGroup.logic}
              </div>
            ))}
          </div>
          <div className="space-y-0.5">
            {filterGroup.children.map((child, index) =>
              "field" in child ? (
                <>
                  {child.field === "project-owner" && (
                    <ProjectOwnerField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                      projectOwnerFilterModel={
                        filterModels.find(
                          (f) => f.field === "project-owner"
                        ) as ProjectOwnerFilterModel
                      }
                      // phase={phase}
                    />
                  )}
                  {child.field === "project-template" && (
                    <ProjectTemplateField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                      projectTemplateFilterModel={
                        filterModels.find(
                          (f) => f.field === "project-template"
                        ) as ProjectTemplateFilterModel
                      }
                      // phase={phase}
                    />
                  )}
                  {child.field === "space" && (
                    <SpaceField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                      spaceFilterModel={
                        filterModels.find(
                          (f) => f.field === "space"
                        ) as SpaceFilterModel
                      }
                      // phase={phase}
                    />
                  )}
                  {child.field === "company" && (
                    <CompanyField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                    />
                  )}

                  {/* {child.field === "project-status" && (
                    <ProjectStatusField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                    />
                  )} */}
                  {/* <div className="flex items-start space-x-2">
                    <FilterField
                      filter={child}
                      index={index}
                      filterGroup={filterGroup}
                      setFilterGroup={setFilterGroup}
                      phase={phase}
                    />
                  </div> */}
                </>
              ) : (
                // <div key={index} style={{ marginLeft: "20px" }}>
                //   <label>
                //     Field:
                //     <input
                //       type="text"
                //       value={child.field}
                //       onChange={(e) => handleFieldChange(index, e.target.value)}
                //     />
                //   </label>
                //   <label>
                //     Comparator:
                //     <input
                //       type="text"
                //       value={JSON.stringify(child.comparator)}
                //       onChange={(e) =>
                //         handleComparatorChange(
                //           index,
                //           JSON.parse(e.target.value)
                //         )
                //       }
                //     />
                //   </label>
                //   <button onClick={() => removeChild(index)}>Remove</button>
                // </div>
                // <div key={index}>
                //   <FilterGroup
                //     filterGroup={child}
                //     setFilterGroup={(updatedChild: FilterGroup) =>
                //       handleChildGroupChange(index, updatedChild)
                //     }
                //     level={level + 1}
                //   />
                //   <button onClick={() => removeChild(index)}>
                //     Remove Group
                //   </button>
                // </div>
                <></>
              )
            )}
            {startDateFilters.length > 0 && (
              <StartDateField
                filters={startDateFilters}
                filterGroup={filterGroup}
                setFilterGroup={setFilterGroup}
              />
            )}
            {progressFilters.length > 0 && (
              <ProjectProgressField
                filters={progressFilters}
                filterGroup={filterGroup}
                setFilterGroup={setFilterGroup}
              />
            )}
            {filterGroup.children.length < filterModels.length && (
              <div
                className={clsx(filterGroup.children.length > 1 && "pt-2.5")}
              >
                <DropdownMenu
                  open={open}
                  onOpenChange={(isOpen) => {
                    setOpen(isOpen);
                  }}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant={"secondary"}
                      className={clsx(
                        filterGroup.children.length === 1 &&
                          "font-normal text-gray-400/70"
                      )}
                    >
                      {filterGroup.children.length > 1
                        ? "Add Filter"
                        : "Select Filter"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuGroup>
                      {filterModels.map((filterModel, index) => {
                        switch (filterModel.field) {
                          case "project-owner":
                            if (
                              filterGroup.children.find(
                                (f) => f.field === "project-owner"
                              )
                            ) {
                              return null;
                            }
                            return (
                              <DropdownMenuItem
                                onSelect={() => {
                                  addFilter("project-owner");
                                  setOpen(false);
                                }}
                              >
                                <UserIcon className="mr-2 h-3.5 w-3.5" />
                                <span>Owner</span>
                              </DropdownMenuItem>
                            );

                          case "company":
                            if (
                              filterGroup.children.find(
                                (f) => f.field === "company"
                              )
                            ) {
                              return null;
                            }
                            return (
                              <DropdownMenuItem
                                onSelect={() => {
                                  addFilter("company");
                                  setOpen(false);
                                }}
                              >
                                <BuildingOffice2Icon className="mr-2 h-3.5 w-3.5" />
                                Company
                              </DropdownMenuItem>
                            );
                          case "project-template":
                            if (
                              filterGroup.children.find(
                                (f) => f.field === "project-template"
                              )
                            ) {
                              return null;
                            }
                            return (
                              <DropdownMenuItem
                                onSelect={() => {
                                  addFilter("project-template");
                                  setOpen(false);
                                }}
                              >
                                <RectangleStackIcon className="mr-2 h-3.5 w-3.5" />
                                Template
                              </DropdownMenuItem>
                            );
                          case "space":
                            if (
                              filterGroup.children.find(
                                (f) => f.field === "space"
                              )
                            ) {
                              return null;
                            }
                            return (
                              <DropdownMenuItem
                                onSelect={() => {
                                  addFilter("space");
                                  setOpen(false);
                                }}
                              >
                                <div className="mr-2 w-3.5">
                                  <SpaceSymbol color="gray" />
                                </div>
                                Space
                              </DropdownMenuItem>
                            );
                          case "project-start-date":
                            if (
                              filterGroup.children.find(
                                (f) => f.field === "project-start-date"
                              )
                            ) {
                              return null;
                            }
                            return (
                              <DropdownMenuItem
                                onSelect={() => {
                                  addFilter("project-start-date");
                                  setOpen(false);
                                }}
                              >
                                <div className="mr-2 w-3.5">
                                  <CalendarIcon className="h-3.5 w-3.5" />
                                </div>
                                Start Date
                              </DropdownMenuItem>
                            );
                          case "project-progress":
                            if (
                              filterGroup.children.find(
                                (f) => f.field === "project-progress"
                              )
                            ) {
                              return null;
                            }
                            return (
                              <DropdownMenuItem
                                onSelect={() => {
                                  addFilter("project-progress");
                                  setOpen(false);
                                }}
                              >
                                <div className="mr-2 w-3.5">
                                  <ProgressIcon />
                                </div>
                                Progress
                              </DropdownMenuItem>
                            );
                          default:
                            return null;
                        }
                      })}
                      {/* <DropdownMenuItem
                      onSelect={() => {
                        addFilter("status");
                        setOpen(false);
                      }}
                    >
                      Status
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => {
                        addFilter("dueDate");
                        setOpen(false);
                      }}
                    >
                      Due Date
                    </DropdownMenuItem> */}
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
            {/* <Select
              value={undefined}
              onValueChange={(value) => {
                addFilter(value as Filter["field"]);
              }}
              open={open}
              onOpenChange={(isOpen) => {
                setOpen(isOpen);
              }}
            >
              <SelectTrigger
                className={clsx(
                  // "flex h-8 w-32 items-center bg-white px-3",
                  "w-fit"
                )}
                showIcon={false}
              >
                {"Add Filter"}
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="dueDate">Due Date</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select> */}
            {/* <Button variant={"secondary"} onClick={addFilter}>
              
            </Button> */}
          </div>
        </div>
      </div>
    </div>
  );
}

const statuses: PhaseStatus[] = [
  "Paused",
  "In Progress",
  "Completed",
  "Canceled",
];

function CompanyField({
  filter,
  index,
  filterGroup,
  setFilterGroup,
}: // filterModels,
{
  filter: CompanyFilter;
  index: number;
  filterGroup: ProjectsV2FilterGroup;
  setFilterGroup: (filterGroup: ProjectsV2FilterGroup) => void;
}) {
  const [valueOpen, setValueOpen] = useState(!filter.value?.length);

  const removeChild = (index: number) => {
    const newChildren = filterGroup.children.filter((_, i) => i !== index);
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleComparatorChange = (comparator: IdComparator) => {
    let newChildren = [...filterGroup.children];
    let currentChild = newChildren[index] as ProjectOwnerFilter;
    currentChild.comparator = comparator;
    newChildren[index] = currentChild;
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleValueChange = (value: string) => {
    // setFilterGroup({
    //   ...filterGroup,
    //   children: filterGroup.children.map((child, i) => {
    //     if (i === index) {
    //       return {
    //         ...child,
    //         value: child.value.includes(value)
    //           ? [...(child as CompanyFilter).value.filter((v) => v !== value)]
    //           : [...(child as CompanyFilter).value, value],
    //       } as CompanyFilter;
    //     }
    //     return child;
    //   }),
    // });
    setFilterGroup({
      ...filterGroup,
      children: [
        ...filterGroup.children.filter((child) => child.field !== "company"),
        {
          field: "company",
          comparator: "in",
          value: filter.value.includes(value)
            ? filter.value.filter((v) => v !== value)
            : [...filter.value, value],
        },
      ],
    });
  };

  const [options, setOptions] = useState<CustomerData[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<CustomerData[]>(
    []
  );

  const [customerSearch, setCustomerSearch] = useState("");

  const { debouncedValue: debouncedSearch, isTyping } = useDebounce(
    customerSearch,
    500
  );

  const { paginatedCustomers, isError, isFetching, isLoading, isSuccess } =
    useGetCustomersWithDebouncing({
      search: debouncedSearch,
    });

  useEffect(() => {
    if (paginatedCustomers) {
      setOptions(paginatedCustomers.customers);
    }
  }, [paginatedCustomers]);

  return (
    <div className="flex items-center space-x-0 text-sm">
      <div className="flex h-8 w-32 items-center truncate rounded-l-md border px-3 text-sm">
        <BuildingOffice2Icon className="mr-1.5 h-3.5 w-3.5" />
        Company
      </div>
      <Select
        value={filter.comparator}
        onValueChange={(value) => {
          handleComparatorChange(value as IdComparator);
        }}
      >
        <SelectTrigger
          className={`flex h-8 ${FIELD_COMPARATOR_WIDTH} items-center truncate rounded-none border-y border-x-0 border-input bg-white px-3`}
          showIcon={false}
        >
          {filter.comparator === "in"
            ? "is"
            : filter.comparator === "nin"
            ? "is not"
            : ""}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="in">is</SelectItem>
            <SelectItem value="nin">is not</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>

      <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
        <PopoverTrigger
          className={`flex h-8 ${FIELD_VALUE_WIDTH} items-center justify-start border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none`}
        >
          {!!filter.value && filter.value.length > 0 ? (
            <div className="truncate">
              {filter.value.length > 1 ? (
                <div>Multiple companies</div>
              ) : (
                <div>
                  {
                    selectedCompanies.find(
                      (o) => o._id.toString() === filter.value[0]
                    )?.name
                  }
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm font-normal text-gray-300">
              Select Company
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent className="pointer-events-auto w-60 p-0" align="start">
          <Command className="relative">
            <CommandInput
              placeholder="Search company..."
              className="h-9"
              value={customerSearch}
              onValueChange={(s) => {
                setCustomerSearch(s);
              }}
            />
            <CommandList>
              {(isFetching || isTyping) && (
                <div className="absolute top-0 right-0 flex items-center pr-2.5 pt-2.5">
                  <Loading className="h-4 w-4 border-[3px]" />
                </div>
              )}
              {!isFetching && !isTyping && (
                <CommandEmpty>No company found.</CommandEmpty>
              )}
              <CommandGroup>
                {options.map((company) => (
                  <CommandItem
                    key={company._id.toString()}
                    value={company.name}
                    onSelect={() => {
                      handleValueChange(company._id.toString());
                      if (
                        selectedCompanies.find(
                          (o) => o._id.toString() === company._id.toString()
                        )
                      ) {
                        setSelectedCompanies((prev) =>
                          prev.filter(
                            (o) => o._id.toString() !== company._id.toString()
                          )
                        );
                      } else {
                        setSelectedCompanies([...selectedCompanies, company]);
                      }
                    }}
                    className="gap-2"
                  >
                    <Checkbox
                      // onClick={(e) => {
                      //   // e.stopPropagation();
                      // }}
                      checked={filter.value.includes(company._id.toString())}
                      // onCheckedChange={(_) => {
                      //   handleValueChange(company._id.toString());
                      // }}
                    />

                    <div className="flex items-center space-x-2">
                      <span className="truncate">{company.name}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div
        className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-2 transition-colors hover:bg-gray-100"
        onClick={() => {
          removeChild(index);
        }}
      >
        <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
      </div>
    </div>
  );
}

type StartDateLocalComparator = "is before" | "is after" | "is between";

function StartDateField({
  filters,
  filterGroup,
  setFilterGroup,
}: // filterModels,
{
  filters: StartDateFilter[];
  filterGroup: ProjectsV2FilterGroup;
  setFilterGroup: (filterGroup: ProjectsV2FilterGroup) => void;
  // filterModels: FilterModel[];
}) {
  const [valueOpen, setValueOpen] = useState(() => {
    const lessThanFilter = filters.find((f) => f.comparator === "lte");
    const greaterThanFilter = filters.find((f) => f.comparator === "gte");
    if (filters.length > 1) {
      if (!!lessThanFilter && !!greaterThanFilter) {
        return !(lessThanFilter.value && greaterThanFilter.value);
      }
    } else if (!!lessThanFilter) {
      return !lessThanFilter.value;
    } else if (!!greaterThanFilter) {
      return !greaterThanFilter.value;
    }
    return false;
  });
  const [localComparator, setLocalComparator] =
    useState<StartDateLocalComparator>(() => {
      const lessThanFilter = filters.find((f) => f.comparator === "lte");
      const greaterThanFilter = filters.find((f) => f.comparator === "gte");
      if (filters.length > 1) {
        if (!!lessThanFilter && !!greaterThanFilter) {
          return "is between";
        }
      } else if (!!lessThanFilter) {
        return "is before";
      } else if (!!greaterThanFilter) {
        return "is after";
      }
      return "is before";
    });

  const removeChild = () => {
    const newChildren = filterGroup.children.filter(
      (f) => f.field !== "project-start-date"
    );
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleComparatorChange = (comparator: StartDateLocalComparator) => {
    const lessThanFilter = filters.find((f) => f.comparator === "lte");
    const greaterThanFilter = filters.find((f) => f.comparator === "gte");
    if (comparator === "is before") {
      setLocalComparator("is before");
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children.filter(
            (f) => f.field !== "project-start-date"
          ),
          {
            field: "project-start-date",
            comparator: "lte",
            value: lessThanFilter?.value ?? greaterThanFilter?.value ?? "",
          },
        ],
      });
    } else if (comparator === "is after") {
      setLocalComparator("is after");
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children.filter(
            (f) => f.field !== "project-start-date"
          ),
          {
            field: "project-start-date",
            comparator: "gte",
            value: greaterThanFilter?.value ?? lessThanFilter?.value ?? "",
          },
        ],
      });
    } else if (comparator === "is between") {
      setLocalComparator("is between");
      setFilterGroup({
        ...filterGroup,
        children: [
          ...filterGroup.children.filter(
            (f) => f.field !== "project-start-date"
          ),
          {
            field: "project-start-date",
            comparator: "gte",
            value: greaterThanFilter?.value ?? dayjs().toISOString(),
          },
          {
            field: "project-start-date",
            comparator: "lte",
            value: lessThanFilter?.value ?? "",
          },
        ],
      });
    }
  };

  const handleValueChange = (value: string) => {
    setFilterGroup({
      ...filterGroup,
      children: filterGroup.children.map((child, i) => {
        if (child.field === "project-start-date") {
          return {
            ...child,
            value: value,
          };
        }
        return child;
      }),
    });
  };

  const handleRangeValueChange = (startDate: string, endDate: string) => {
    setFilterGroup({
      ...filterGroup,
      children: [
        ...filterGroup.children.filter((f) => f.field !== "project-start-date"),
        {
          field: "project-start-date",
          comparator: "gte",
          value: startDate,
        },
        {
          field: "project-start-date",
          comparator: "lte",
          value: endDate,
        },
      ],
    });
  };

  const lessThanFilter = useMemo(
    () => filters.find((f) => f.comparator === "lte"),
    [filters]
  );
  const greaterThanFilter = useMemo(
    () => filters.find((f) => f.comparator === "gte"),
    [filters]
  );
  return (
    <div className="flex items-center space-x-0 text-sm">
      <div className="flex h-8 w-32 items-center truncate rounded-l-md border px-3 text-sm">
        <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
        Start Date
      </div>
      <Select
        value={localComparator}
        onValueChange={(value) => {
          handleComparatorChange(value as StartDateLocalComparator);
        }}
      >
        <SelectTrigger
          className={`flex h-8 ${FIELD_COMPARATOR_WIDTH} items-center truncate rounded-none border-y border-x-0 border-input bg-white px-3`}
          showIcon={false}
        >
          {localComparator === "is before"
            ? "is before"
            : localComparator === "is after"
            ? "is after"
            : "is between"}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="is before">is before</SelectItem>
            <SelectItem value="is after">is after</SelectItem>
            {/* <SelectItem value="is between">is between</SelectItem> */}
          </SelectGroup>
        </SelectContent>
      </Select>

      {localComparator === "is before" && lessThanFilter && (
        <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
          <PopoverTrigger
            className={`flex h-8 ${FIELD_VALUE_WIDTH} items-center justify-start border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none`}
          >
            {!!lessThanFilter.value ? (
              <>{dayjs(lessThanFilter.value).format("DD MMM YYYY")}</>
            ) : (
              <div className="text-sm font-normal text-gray-300">
                Select Date
              </div>
            )}
          </PopoverTrigger>
          <PopoverContent
            className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
            align="start"
          >
            <Calendar
              mode="single"
              selected={dayjs(lessThanFilter.value).toDate()}
              onSelect={(date) => {
                handleValueChange(dayjs(date).toISOString());
              }}
            />
          </PopoverContent>
        </Popover>
      )}
      {localComparator === "is after" && greaterThanFilter && (
        <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
          <PopoverTrigger
            className={`flex h-8 ${FIELD_VALUE_WIDTH} items-center justify-start border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none`}
          >
            {!!greaterThanFilter.value ? (
              <>{dayjs(greaterThanFilter.value).format("DD MMM YYYY")}</>
            ) : (
              <div className="text-sm font-normal text-gray-300">
                Select Date
              </div>
            )}
          </PopoverTrigger>
          <PopoverContent
            className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
            align="start"
          >
            <Calendar
              mode="single"
              selected={dayjs(greaterThanFilter.value).toDate()}
              onSelect={(date) => {
                handleValueChange(dayjs(date).toISOString());
              }}
            />
          </PopoverContent>
        </Popover>
      )}
      {localComparator === "is between" && (
        <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
          <PopoverTrigger className="flex h-8 w-52 items-center justify-start border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none">
            {!!greaterThanFilter?.value || !!lessThanFilter?.value ? (
              <>
                {dayjs(greaterThanFilter?.value).format("DD MMM YYYY")} -{" "}
                {dayjs(lessThanFilter?.value).format("DD MMM YYYY")}
              </>
            ) : (
              <div className="text-sm font-normal text-gray-300">
                Select Dates
              </div>
            )}
          </PopoverTrigger>
          <PopoverContent
            className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
            align="start"
          >
            <Calendar
              mode="range"
              // selected={
              //   [
              //     dayjs(greaterThanFilter.value).toDate(),
              //     dayjs(lessThanFilter.value).toDate(),
              //   ]
              // }
              selected={{
                from: dayjs(greaterThanFilter?.value).toDate(),
                to: dayjs(lessThanFilter?.value).toDate(),
              }}
              onSelect={(range) => {
                console.log(range);
                if (range) {
                  handleRangeValueChange(
                    dayjs(range.from).toISOString(),
                    dayjs(range.to).toISOString()
                  );
                }
              }}
              min={2}
            />
          </PopoverContent>
        </Popover>
      )}

      <div
        className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-2 transition-colors hover:bg-gray-100"
        onClick={() => {
          removeChild();
        }}
      >
        <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
      </div>
    </div>
  );
}

function ProjectProgressField({
  filters,
  filterGroup,
  setFilterGroup,
}: // filterModels,
{
  filters: ProjectProgressFilter[];
  filterGroup: ProjectsV2FilterGroup;
  setFilterGroup: (filterGroup: ProjectsV2FilterGroup) => void;
  // filterModels: FilterModel[];
}) {
  const [valueOpen, setValueOpen] = useState(false);

  const removeChild = () => {
    const newChildren = filterGroup.children.filter(
      (f) => f.field !== "project-progress"
    );
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleRangeValueChange = (start: number, end: number) => {
    setFilterGroup({
      ...filterGroup,
      children: [
        ...filterGroup.children.filter((f) => f.field !== "project-progress"),
        {
          field: "project-progress",
          comparator: "gte",
          value: start,
        },
        {
          field: "project-progress",
          comparator: "lte",
          value: end,
        },
      ],
    });
  };

  const lessThanFilter = useMemo(
    () => filters.find((f) => f.comparator === "lte"),
    [filters]
  );
  const greaterThanFilter = useMemo(
    () => filters.find((f) => f.comparator === "gte"),
    [filters]
  );

  return (
    <div className="flex items-center space-x-0 text-sm">
      <div className="flex h-8 w-32 items-center space-x-1 truncate rounded-l-md border px-3 text-sm">
        <ProgressIcon />
        <div>Progress</div>
      </div>
      <Select
        value={"is"}
        // onValueChange={(value) => {
        //   handleComparatorChange(value as StartDateLocalComparator);
        // }}
      >
        <SelectTrigger
          className={`flex h-8 ${FIELD_COMPARATOR_WIDTH} items-center truncate rounded-none border-y border-x-0 border-input bg-white px-3`}
          showIcon={false}
        >
          {"is between"}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="is">is between</SelectItem>
            {/* <SelectItem value="is after">is after</SelectItem> */}
            {/* <SelectItem value="is between">is between</SelectItem> */}
          </SelectGroup>
        </SelectContent>
      </Select>
      <div
        className={`flex h-8 ${FIELD_VALUE_WIDTH} flex flex-1 items-center justify-start space-x-2 border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none`}
      >
        <div className="w-5 text-left text-xs">
          {greaterThanFilter?.value ?? 0}
        </div>
        {/* <div className="h-[3px] rounded-md flex-1 bg-gray-200 relative">
              <div className="h-[3px] bg-primary absolute"
                style={{
                  left: `${greaterThanFilter?.value ?? 0}%`,
                  right: `${100 - (lessThanFilter?.value ?? 100)}%`,
                }}
              />
            </div> */}
        <Slider.Root
          min={0}
          max={100}
          className="relative flex h-5 flex-1 touch-none select-none items-center"
          value={[greaterThanFilter?.value ?? 0, lessThanFilter?.value ?? 100]}
          onValueChange={(v) => {
            handleRangeValueChange(v[0], v[1]);
          }}
          // onValueCommit={(v) => {
          //   projectPerformanceCtx.dispatch({
          //     type: "revenue_range_update",
          //     revenueRange: {
          //       ...projectPerformanceCtx.state.revenueRange,
          //       currentMin: v[0],
          //       currentMax: v[1],
          //     },
          //   });
          // }}
          step={10}
        >
          <Slider.Track className="relative h-[3px] grow rounded-full bg-gray-200">
            <Slider.Range className="absolute h-full rounded-full bg-primary" />
          </Slider.Track>
          <Slider.Thumb className="block h-4 w-4 rounded-full border border-primary/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50" />
          <Slider.Thumb className="block h-4 w-4 rounded-full border border-primary/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50" />
        </Slider.Root>
        <div className="w-5 text-right text-xs">
          {lessThanFilter?.value ?? 100}
        </div>
      </div>
      {/* <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
        <PopoverTrigger
          className={``}
        >
          
        </PopoverTrigger>
        <PopoverContent
          className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
          align="start"
        >
          <div className="p-4">
            
          </div>
        </PopoverContent>
      </Popover> */}
      <div
        className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-2 transition-colors hover:bg-gray-100"
        onClick={() => {
          removeChild();
        }}
      >
        <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
      </div>
    </div>
  );
}
function ProjectTemplateField({
  filter,
  index,
  filterGroup,
  setFilterGroup,
  projectTemplateFilterModel,
}: // filterModels,
{
  filter: ProjectTemplateFilter;
  index: number;
  filterGroup: ProjectsV2FilterGroup;
  setFilterGroup: (filterGroup: ProjectsV2FilterGroup) => void;
  // filterModels: FilterModel[];
  projectTemplateFilterModel: ProjectTemplateFilterModel;
}) {
  const [open, setOpen] = useState(!filter.field);
  const [valueOpen, setValueOpen] = useState(!filter.value?.length);
  const type = filter.field;

  const removeChild = (index: number) => {
    const newChildren = filterGroup.children.filter((_, i) => i !== index);
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleComparatorChange = (comparator: IdComparator) => {
    let newChildren = [...filterGroup.children];
    let currentChild = newChildren[index] as ProjectOwnerFilter;
    currentChild.comparator = comparator;
    newChildren[index] = currentChild;
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleValueChange = (value: string) => {
    // setFilterGroup({
    //   ...filterGroup,
    //   children: filterGroup.children.map((child, i) => {
    //     if (i === index) {
    //       return {
    //         ...child,
    //         value: child.value.includes(value)
    //           ? [
    //               ...(child as ProjectTemplateFilter).value.filter(
    //                 (v) => v !== value
    //               ),
    //             ]
    //           : [...(child as ProjectTemplateFilter).value, value],
    //       } as ProjectTemplateFilter;
    //     }
    //     return child;
    //   }),
    // });
    setFilterGroup({
      ...filterGroup,
      children: [
        ...filterGroup.children.filter(
          (child) => child.field !== "project-template"
        ),
        {
          field: "project-template",
          comparator: "in",
          value: filter.value.includes(value)
            ? filter.value.filter((v) => v !== value)
            : [...filter.value, value],
        },
      ],
    });
  };

  return (
    <div className="flex items-center space-x-0 text-sm">
      <div className="flex h-8 w-32 items-center truncate rounded-l-md border px-3 text-sm">
        <RectangleStackIcon className="mr-1.5 h-3.5 w-3.5" />
        Template
      </div>
      <Select
        value={filter.comparator}
        onValueChange={(value) => {
          handleComparatorChange(value as IdComparator);
        }}
      >
        <SelectTrigger
          className={`flex h-8 ${FIELD_COMPARATOR_WIDTH} items-center truncate rounded-none border-y border-x-0 border-input bg-white px-3`}
          showIcon={false}
        >
          {filter.comparator === "in"
            ? "is"
            : filter.comparator === "nin"
            ? "is not"
            : ""}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="in">is</SelectItem>
            <SelectItem value="nin">is not</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>

      <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
        <PopoverTrigger
          className={`flex h-8 ${FIELD_VALUE_WIDTH} items-center justify-start border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none`}
        >
          {!!filter.value && filter.value.length > 0 ? (
            <div className="truncate">
              {filter.value.length > 1 ? (
                <div>Multiple Templates</div>
              ) : (
                projectTemplateFilterModel.projectTemplates.find(
                  (o) => o._id === filter.value[0]
                )?.name
              )}
            </div>
          ) : (
            <div className="text-sm font-normal text-gray-300">
              Select Template
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search template..." className="h-9" />
            <CommandList>
              <CommandEmpty>No template found.</CommandEmpty>
              <CommandGroup>
                {projectTemplateFilterModel.projectTemplates.map(
                  (projectTemplate) => (
                    <CommandItem
                      key={projectTemplate._id}
                      value={projectTemplate.name}
                      onSelect={() => {
                        handleValueChange(projectTemplate._id);
                      }}
                      className="gap-2"
                    >
                      <Checkbox
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        checked={filter.value.includes(projectTemplate._id)}
                        onCheckedChange={(_) => {
                          handleValueChange(projectTemplate._id);
                        }}
                      />

                      <div className="flex items-center space-x-2">
                        <span className="truncate">{projectTemplate.name}</span>
                      </div>
                    </CommandItem>
                  )
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div
        className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-2 transition-colors hover:bg-gray-100"
        onClick={() => {
          removeChild(index);
        }}
      >
        <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
      </div>
    </div>
  );
}

function SpaceField({
  filter,
  index,
  filterGroup,
  setFilterGroup,
  spaceFilterModel,
}: // filterModels,
{
  filter: SpaceFilter;
  index: number;
  filterGroup: ProjectsV2FilterGroup;
  setFilterGroup: (filterGroup: ProjectsV2FilterGroup) => void;
  // filterModels: FilterModel[];
  spaceFilterModel: SpaceFilterModel;
}) {
  const [open, setOpen] = useState(!filter.field);
  const [valueOpen, setValueOpen] = useState(!filter.value?.length);
  const type = filter.field;

  const removeChild = (index: number) => {
    const newChildren = filterGroup.children.filter((_, i) => i !== index);
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleComparatorChange = (comparator: IdComparator) => {
    let newChildren = [...filterGroup.children];
    let currentChild = newChildren[index] as ProjectOwnerFilter;
    currentChild.comparator = comparator;
    newChildren[index] = currentChild;
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleValueChange = (value: string) => {
    // setFilterGroup({
    //   ...filterGroup,
    //   children: filterGroup.children.map((child, i) => {
    //     if (i === index) {
    //       return {
    //         ...child,
    //         value: child.value.includes(value)
    //           ? [...(child as SpaceFilter).value.filter((v) => v !== value)]
    //           : [...(child as SpaceFilter).value, value],
    //       } as SpaceFilter;
    //     }
    //     return child;
    //   }),
    // });
    setFilterGroup({
      ...filterGroup,
      children: [
        ...filterGroup.children.filter((child) => child.field !== "space"),
        {
          field: "space",
          comparator: "in",
          value: filter.value.includes(value)
            ? filter.value.filter((v) => v !== value)
            : [...filter.value, value],
        },
      ],
    });
  };

  return (
    <div className="flex items-center space-x-0 text-sm">
      <div className="flex h-8 w-32 items-center space-x-2 truncate rounded-l-md border px-3 text-sm">
        <SpaceSymbol color="gray" />
        <div>Space</div>
      </div>
      <Select
        value={filter.comparator}
        onValueChange={(value) => {
          handleComparatorChange(value as IdComparator);
        }}
      >
        <SelectTrigger
          className={`flex h-8 ${FIELD_COMPARATOR_WIDTH} items-center truncate rounded-none border-y border-x-0 border-input bg-white px-3`}
          showIcon={false}
        >
          {filter.comparator === "in"
            ? "is"
            : filter.comparator === "nin"
            ? "is not"
            : ""}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="in">is</SelectItem>
            <SelectItem value="nin">is not</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>

      <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
        <PopoverTrigger
          className={`flex h-8 ${FIELD_VALUE_WIDTH} items-center justify-start border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none`}
        >
          {!!filter.value && filter.value.length > 0 ? (
            <div className="truncate">
              {filter.value.length > 1 ? (
                <div className="flex items-center space-x-0.5">
                  {filter.value.map((o) => {
                    return (
                      <>
                        {filter.value.length === 1 ? (
                          <SpaceNameAndSymbol
                            color={
                              spaceFilterModel.spaces.find((s) => s._id === o)
                                ?.color ?? "gray"
                            }
                            name={
                              spaceFilterModel.spaces.find((s) => s._id === o)
                                ?.name ?? ""
                            }
                          />
                        ) : (
                          <div
                            key={o}
                            className="flex h-4 w-4 items-center justify-center"
                          >
                            <SpaceSymbol
                              color={
                                spaceFilterModel.spaces.find((s) => s._id === o)
                                  ?.color ?? "gray"
                              }
                            />
                          </div>
                        )}
                      </>
                    );
                  })}
                </div>
              ) : (
                spaceFilterModel.spaces.find((o) => o._id === filter.value[0])
                  ?.name
              )}
            </div>
          ) : (
            <div className="text-sm font-normal text-gray-300">
              Select Space
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search space..." className="h-9" />
            <CommandList>
              <CommandEmpty>No space found.</CommandEmpty>
              <CommandGroup>
                {spaceFilterModel.spaces.map((space) => (
                  <CommandItem
                    key={space._id}
                    value={space.name}
                    onSelect={() => {
                      handleValueChange(space._id);
                    }}
                    className="gap-2"
                  >
                    <Checkbox
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      checked={filter.value.includes(space._id)}
                      onCheckedChange={(_) => {
                        handleValueChange(space._id);
                      }}
                    />

                    <div className="flex items-center space-x-2">
                      <SpaceSymbol color={space.color} />
                      <span className="truncate">{space.name}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div
        className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-2 transition-colors hover:bg-gray-100"
        onClick={() => {
          removeChild(index);
        }}
      >
        <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
      </div>
    </div>
  );
}

function ProjectOwnerField({
  filter,
  index,
  filterGroup,
  setFilterGroup,
  projectOwnerFilterModel,
}: // filterModels,
{
  filter: ProjectOwnerFilter;
  index: number;
  filterGroup: ProjectsV2FilterGroup;
  setFilterGroup: (filterGroup: ProjectsV2FilterGroup) => void;
  // filterModels: FilterModel[];
  projectOwnerFilterModel: ProjectOwnerFilterModel;
}) {
  const [open, setOpen] = useState(!filter.field);
  const [valueOpen, setValueOpen] = useState(!filter.value?.length);
  const type = filter.field;

  const removeChild = (index: number) => {
    const newChildren = filterGroup.children.filter((_, i) => i !== index);
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleComparatorChange = (comparator: IdComparator) => {
    let newChildren = [...filterGroup.children];
    let currentChild = newChildren[index] as ProjectOwnerFilter;
    currentChild.comparator = comparator;
    newChildren[index] = currentChild;
    setFilterGroup({ ...filterGroup, children: newChildren });
  };

  const handleValueChange = (value: string) => {
    // setFilterGroup({
    //   ...filterGroup,
    //   children: filterGroup.children.map((child, i) => {
    //     if (i === index) {
    //       return {
    //         ...child,
    //         value: child.value.includes(value)
    //           ? [
    //               ...(child as ProjectOwnerFilter).value.filter(
    //                 (v) => v !== value
    //               ),
    //             ]
    //           : [...(child as ProjectOwnerFilter).value, value],
    //       } as ProjectOwnerFilter;
    //     }
    //     return child;
    //   }),
    // });
    setFilterGroup({
      ...filterGroup,
      children: [
        ...filterGroup.children.filter(
          (child) => child.field !== "project-owner"
        ),
        {
          field: "project-owner",
          comparator: "in",
          value: filter.value.includes(value)
            ? filter.value.filter((v) => v !== value)
            : [...filter.value, value],
        },
      ],
    });
  };

  return (
    <div className="flex items-center space-x-0 text-sm">
      <div className="flex h-8 w-32 items-center truncate rounded-l-md border px-3 text-sm">
        <UserIcon className="mr-1.5 h-3.5 w-3.5" />
        Owner
      </div>
      <Select
        value={filter.comparator}
        onValueChange={(value) => {
          handleComparatorChange(value as IdComparator);
        }}
      >
        <SelectTrigger
          className={`flex h-8 ${FIELD_COMPARATOR_WIDTH} items-center truncate rounded-none border-y border-x-0 border-input bg-white px-3`}
          showIcon={false}
        >
          {filter.comparator === "in"
            ? "is"
            : filter.comparator === "nin"
            ? "is not"
            : ""}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="in">is</SelectItem>
            <SelectItem value="nin">is not</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>

      <Popover modal={true} open={valueOpen} onOpenChange={setValueOpen}>
        <PopoverTrigger
          className={`flex h-8 ${FIELD_VALUE_WIDTH} items-center justify-start border-y border-l bg-white px-3 text-sm hover:bg-gray-50 focus:outline-none`}
        >
          {!!filter.value && filter.value.length > 0 ? (
            <div className="truncate">
              {filter.value.length > 1 ? (
                <div className="flex items-center space-x-0.5">
                  {filter.value.map((o) => {
                    return (
                      <div
                        key={o}
                        className="flex h-6 w-6 items-center justify-center rounded border bg-gray-100 text-xs uppercase text-gray-600 shadow-sm"
                      >
                        {getFirstAndLastInitials(
                          projectOwnerFilterModel.projectOwners.find(
                            (po) => po._id === o
                          )?.name ?? ""
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                projectOwnerFilterModel.projectOwners.find(
                  (o) => o._id === filter.value[0]
                )?.name
              )}
            </div>
          ) : (
            <div className="text-sm font-normal text-gray-300">
              Select Owner
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search owner..." className="h-9" />
            <CommandList>
              <CommandEmpty>No owner found.</CommandEmpty>
              <CommandGroup>
                {projectOwnerFilterModel.projectOwners.map((projectOwner) => (
                  <CommandItem
                    key={projectOwner._id}
                    value={projectOwner.name}
                    onSelect={() => {
                      handleValueChange(projectOwner._id);
                    }}
                    className="gap-2"
                  >
                    <Checkbox
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      checked={filter.value.includes(projectOwner._id)}
                      onCheckedChange={(_) => {
                        handleValueChange(projectOwner._id);
                      }}
                    />

                    <div className="flex items-center space-x-2">
                      <span className="truncate">{projectOwner.name}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div
        className="flex h-8 w-fit cursor-pointer items-center rounded-r-md border bg-white px-2 transition-colors hover:bg-gray-100"
        onClick={() => {
          removeChild(index);
        }}
      >
        <XMarkIcon className="h-2.5 w-2.5 shrink-0" />
      </div>
    </div>
  );
}

type Project = ProjectDataV2 & ProjectDetailsData;

export function GeneralProjectsList({
  projects,
  // filterGroup,
  columnsToDisplay,
  projectDetails,
  // search,
  // setSearch,
  // setNoOfProjectsAfterSearch,
  setColumnsToDisplay,
  sort,
  setSort,
}: {
  projects: ProjectDataV2[];
  // filterGroup: ProjectsV2FilterGroup;
  columnsToDisplay: ColumnsToDisplay;
  projectDetails: ProjectDetailsData[] | undefined;
  // search: string;
  // setSearch: (search: string) => void;
  // setNoOfProjectsAfterSearch: React.Dispatch<React.SetStateAction<number>>;
  setColumnsToDisplay: React.Dispatch<React.SetStateAction<ColumnsToDisplay>>;
  sort: ProjectsSort;
  setSort: (sort: ProjectsSort) => void;
}) {
  // console.log(columnsToDisplay);
  const { data: customFields } = useGetCustomFields("project");
  // const [sorting, setSorting] = useState<SortingState>([]);
  const data = useMemo<Project[]>(() => {
    if (!projects) return [];
    // return projects;
    return projects.map((project) => ({
      ...project,
      ...(projectDetails?.find(
        (projectDetails) => project._id === projectDetails._id
      ) || {}),
    }));
  }, [projects, projectDetails]);
  const { data: settings } = useGetSettings();

  const columns = useMemo(() => {
    return getColumns(
      columnsToDisplay,
      customFields ?? [],
      settings,
      sort,
      setSort
    );
  }, [columnsToDisplay, customFields, settings, sort, setSort]);

  const table = useReactTable<Project>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      // sorting,
      // globalFilter: search,
      columnVisibility: transformColumnsToDisplay(columnsToDisplay),
    },
    // globalFilterFn: fuzzyFilter,
    // onSortingChange: setSorting,
    // onGlobalFilterChange: setSearch,
    // getSortedRowModel: getSortedRowModel(),
    // getFilteredRowModel: getFilteredRowModel(),
    // onColumnVisibilityChange: setColumnsToDisplay,
  });
  const navigate = useNavigate();

  // useEffect(() => {
  //   setNoOfProjectsAfterSearch(table.getRowModel().rows.length);
  // }, [table.getRowModel().rows]);

  if (table.getRowModel().rows.length === 0) {
    return <NoProjectsMeetFilter />;
  }

  return (
    <div className="overflow-auto">
      <div className="max-h-full max-w-full overflow-auto">
        <table
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
          className="min-w-[600px]"
        >
          <thead className="overflow-auto">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={
                      "sticky top-0 z-10 h-11 whitespace-nowrap border-b border-r border-gray-200/70 bg-gray-50 px-6 text-left text-xs font-normal uppercase tracking-wide text-gray-400 first:left-0 first:z-20 first:w-full first:pl-8"
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className=" divide-gray-200 bg-white">
            {!projects ? (
              <tr>
                {/* <td className="inset-0 flex items-center justify-center p-4 pl-8"> */}
                <div className="sticky left-0 z-10 p-4 pl-8 text-gray-400">
                  Loading...
                </div>
                {/* </td> */}
              </tr>
            ) : (
              <>
                {projects.length === 0 ? (
                  <tr>
                    <div className="sticky left-0 z-10 whitespace-nowrap p-4 pl-8 text-gray-400">
                      No projects
                    </div>
                    {/* <td className="inset-0 flex items-center justify-center p-4">
                </td> */}
                  </tr>
                ) : (
                  <>
                    {table.getRowModel().rows.map((row, rowIdx) => (
                      <tr
                        key={row.id}
                        className="group relative rounded-lg hover:cursor-pointer hover:bg-gray-50"
                        onClick={() => {
                          navigate(`/projects/${row.original._id}/home`);
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={clsx(
                              "whitespace-nowrap border-r border-b border-gray-100 px-6 py-3 text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:bg-white first:pr-6 first:font-medium first:text-gray-900 first:group-hover:bg-gray-50 first:sm:pl-8"
                              // rowIdx === table.getRowModel().rows.length - 1
                              //   ? "border-b-0"
                              //   : ""
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getColumns(
  columnsToDisplay: ColumnsToDisplay,
  customFields: CustomFieldData[],
  settings: SettingsData | undefined,
  sort: ProjectsSort,
  setSort: (sort: ProjectsSort) => void
) {
  let columns: ColumnDef<Project>[] = [
    {
      id: "project",
      header: ({ column }) => {
        return (
          <div className="flex w-72 items-center space-x-1">
            <div>Name</div>
            <NewSortToggle
              isActive={sort.property === "name"}
              order={sort.order}
              setSort={setSort}
              property={"name"}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        return <div className="h-full w-72 truncate">{row.original.name}</div>;
      },
      // accessorFn: ({ name }) => name,
    },
    {
      id: "company",
      header: ({ column }) => {
        return (
          <div className="flex w-52 items-center space-x-1">
            <div>Company</div>
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex h-full w-52 items-center gap-1.5 truncate">
            <div className="min-w-0 truncate">
              {row.original.customer?.name}
            </div>
            {row.original.customer?.source === "hubspot" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="3 3.5 12 12"
                className="w-3.5 shrink-0"
              >
                <path
                  fill="#FF5C35"
                  d="M11.54 7.422V6.096a1.02 1.02 0 0 0 .588-.92v-.03c0-.56-.46-1.02-1.02-1.02h-.03c-.561 0-1.02.46-1.02 1.02v.03c0 .407.243.757.589.92v1.326c-.516.08-.986.292-1.375.605l-3.64-2.832a1.14 1.14 0 0 0 .04-.286 1.15 1.15 0 1 0-1.15 1.148c.206 0 .398-.059.566-.154l3.58 2.786a2.892 2.892 0 0 0-.482 1.603c0 .62.196 1.194.527 1.666l-1.089 1.09a.935.935 0 0 0-.27-.045.945.945 0 1 0 .945.945.934.934 0 0 0-.044-.27l1.077-1.077a2.89 2.89 0 0 0 1.761.598 2.905 2.905 0 0 0 .446-5.776Zm-.447 4.36a1.49 1.49 0 1 1 0-2.98 1.49 1.49 0 0 1 0 2.98Z"
                />
              </svg>
            )}
          </div>
        );
      },
      // accessorFn: ({ customer }) => customer?.name,
    },
    {
      id: "internalUpdates",
      header: ({ column }) => (
        <div className="flex items-center space-x-1">
          <div>Updates</div>
          {/* <SortToggle column={column} /> */}
        </div>
      ),
      cell: ({ row }) => {
        return (
          <div className="relative w-32 overflow-hidden">
            <div className="absolute inset-y-0 right-0 h-full w-8 bg-gradient-to-r from-transparent to-white" />
            <HoverCard openDelay={100} closeDelay={100}>
              <HoverCardTrigger asChild>
                <div className="flex h-full w-fit items-center space-x-1.5">
                  {row.original.internalUpdates
                    ?.filter((u) => u.isManual && u.state !== "pending")
                    .map((update, index) => (
                      <InternalUpdateSymbol update={update} index={index} />
                    ))}
                </div>
              </HoverCardTrigger>
              <HoverCardContent
                className="max-h-[calc(100vh-20px)] w-[480px] overflow-y-auto p-0"
                sideOffset={6}
                align="center"
                side="right"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {row.original.internalUpdates
                  ?.filter((u) => u.isManual && u.state !== "pending")
                  .map((update) => (
                    <InternalUpdate update={update} />
                  ))}
              </HoverCardContent>
            </HoverCard>
          </div>
        );
      },
    },
    // {
    //   id: "progress",
    //   header: ({ column }) => (
    //     <div className="flex items-center space-x-1">
    //       <div>Progress</div>
    //       {/* <SortToggle column={column} /> */}
    //     </div>
    //   ),
    //   cell: ({ row }) => {
    //     // return <div>hello??</div>
    //     // return <CellSkeleton width={56 * 4} />;
    //     if (!row.original.journey) return <CellSkeleton width={44 * 4} />;
    //     const progress = Math.round(
    //       (row.original.journey.completedTasks /
    //         row.original.journey.totalTasks) *
    //         100
    //     );
    //     const hasTasks = row.original.journey.totalTasks > 0;
    //     return (
    //       <>

    //       </>
    //     );
    //   },
    //   accessorFn: ({ journey }) => {
    //     if (!journey?.totalTasks) return -1;
    //     const progress = Math.round(
    //       ((journey?.completedTasks ?? 0) / journey.totalTasks) * 100
    //     );
    //     return progress;
    //   },
    // },
    {
      id: "progress",
      header: ({ column }) => (
        <div className="flex items-center space-x-1">
          <div>Progress</div>
          {/* <SortToggle column={column} /> */}
        </div>
      ),
      cell: ({ row }) => {
        if (!row.original.journey) return <CellSkeleton className="w-44" />;
        // return <div>hello??</div>
        // return <CellSkeleton width={56 * 4} />;
        return (
          <div>
            <div className="flex w-44 items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div
                  className={clsx(
                    "h-1.5 w-20 shrink-0 overflow-hidden rounded-sm",
                    Math.round(row.original.journey.completionPercent) === 0
                      ? "border-gray-300 bg-gray-200/70"
                      : "border-green-200 bg-green-200/70"
                  )}
                >
                  <div
                    style={{
                      width:
                        Math.round(row.original.journey?.completionPercent) +
                        "%",
                    }}
                    className="h-full rounded-sm bg-green-500"
                  ></div>
                </div>
                <div className="w-6 text-sm font-medium text-gray-600">
                  {row.original.journey?.completionPercent.toFixed(0) + "%"}
                </div>
              </div>
              <div className="flex h-5 w-fit items-center justify-center rounded-md bg-gray-50 px-1.5">
                <div className="text-[11px] text-gray-400">{`${row.original.journey.completedTasks}/${row.original.journey.totalTasks}`}</div>
              </div>
            </div>
          </div>
        );
      },
      // accessorFn: ({ journey }) => {
      //   if (!journey?.totalTasks) return -1;
      //   const progress = Math.round(
      //     ((journey?.completedTasks ?? 0) / journey.totalTasks) * 100
      //   );
      //   return progress;
      // },
    },
    {
      id: "status",
      header: ({ column }) => {
        return (
          <div className="flex w-32 items-center space-x-1">
            <span>Status</span>
            <NewSortToggle
              isActive={sort.property === "status"}
              order={sort.order}
              setSort={setSort}
              property={"status"}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        const { status } = row.original;
        return (
          <div className="h-full">
            <span>{getPhaseStatus(status)}</span>
          </div>
        );
      },
      accessorFn: ({ status }) => status,
    },
    {
      id: "daysActive",
      header: ({ column }) => {
        return (
          <div className={`flex w-32 items-center space-x-1`}>
            <div>Days Active</div>
            {/* <SortToggle column={column} /> */}
          </div>
        );
      },
      cell: ({ row }) => {
        const numberOfActiveDays = dayjs().diff(
          dayjs(row.original.createdAt),
          "days"
        );
        if (numberOfActiveDays === 0) {
          return <div className="h-full w-32 truncate">{"<1"}</div>;
        }
        return (
          <div className="h-full w-32 truncate">
            {numberOfActiveDays.toFixed(0)}
          </div>
        );
      },
      // accessorFn: ({ createdAt }) => {
      //   return dayjs().diff(dayjs(createdAt), "days");
      // },
    },
    // {
    //   id: "daysActive",
    //   header: ({ column }) => {
    //     return (
    //       <div className={`flex w-32 items-center space-x-1`}>
    //         <div>Days Active</div>
    //         {/* <SortToggle column={column} /> */}
    //       </div>
    //     );
    //   },
    //   cell: ({ row }) => {
    //     const numberOfActiveDays = dayjs().diff(
    //       dayjs(row.original.createdAt),
    //       "days"
    //     );
    //     if (numberOfActiveDays === 0) {
    //       // return in 0.1 increments with rounding up
    //       const numberOfActiveHours = dayjs().diff(
    //         dayjs(row.original.createdAt),
    //         "hours"
    //       );
    //       return (numberOfActiveHours / 24).toFixed(1);
    //     }
    //     return <div className="h-full w-32 truncate">{numberOfActiveDays}</div>;
    //   },
    //   accessorFn: ({ createdAt }) => {
    //     return dayjs().diff(dayjs(createdAt), "days");
    //   },
    // },
    {
      id: "owner",
      header: ({ column }) => {
        return (
          <div className="flex w-44 items-center space-x-1">
            <div>Owner</div>
            {/* <SortToggle column={column} /> */}
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="h-full w-44 truncate">{row.original.owner.name}</div>
        );
      },
      accessorFn: ({ owner }) => owner?.name,
    },
    {
      id: "deltaToTarget",
      header: ({ column }) => {
        return (
          <div className="flex w-44 items-center space-x-1">
            <div>Target vs Forecast</div>
            {/* <SortToggle column={column} /> */}
          </div>
        );
      },
      cell: ({ row }) => {
        if (!row.original.journey) {
          return <CellSkeleton className="w-44" />;
        }

        if (
          !row.original.forecastedCompletionDate ||
          !row.original.assignedEndDate
        )
          return null;

        const delta = getTimeDifference(
          new Date(row.original.forecastedCompletionDate),
          new Date(row.original.assignedEndDate)
        );
        return (
          <div
            className={clsx(
              "flex w-44 items-center space-x-1.5 text-sm",
              delta.isDate2After ? "text-green-500" : "text-red-500"
            )}
          >
            <TriangleUpIcon
              className={clsx(
                "h-4 w-4",
                delta.isDate2After
                  ? "text-green-500"
                  : "rotate-180 transform text-red-500"
              )}
            />
            <div>{`${delta.absValue} ${delta.unit}`}</div>
          </div>
        );
      },
    },
    // {
    //   id: "deltaToTarget",
    //   header: ({ column }) => {
    //     return (
    //       <div className="flex w-44 items-center space-x-1">
    //         <div>Target vs Forecast</div>
    //         {/* <SortToggle column={column} /> */}
    //       </div>
    //     );
    //   },
    //   cell: ({ row }) => {
    //     // if (!row.original.keyDates) return null;
    //     if (
    //       !row.original.assignedEndDate ||
    //       !row.original.journey ||
    //       !settings ||
    //       !!row.original.keyDates?.completionDate
    //     )
    //       return null;
    //     // if (row.original.type === "Simple") {
    //     //   return <div className="text-gray-500">-</div>;
    //     // }
    //     const forecastedEndDate = calculateEndDate(
    //       new Date(),
    //       row.original.journey.durations.forecasted,
    //       settings.workingTimeDetails,
    //       settings.timezone
    //     );

    //     // const completionDelta = getTimeDifference(
    //     //   row.original,
    //     //   new Date(row.original.assignedEndDate)
    //     // );

    //     const delta = getTimeDifference(
    //       forecastedEndDate,
    //       new Date(row.original.assignedEndDate)
    //     );
    //     return (
    //       <div
    //         className={clsx(
    //           "flex items-center space-x-1.5 text-sm",
    //           delta.isDate2After ? "text-green-500" : "text-red-500"
    //         )}
    //       >
    //         <TriangleUpIcon
    //           className={clsx(
    //             "h-4 w-4",
    //             delta.isDate2After
    //               ? "text-green-500"
    //               : "rotate-180 transform text-red-500"
    //           )}
    //         />
    //         <div>{`${delta.absValue} ${delta.unit}`}</div>
    //       </div>
    //       // <>
    //       //   {!!completionDelta ? (
    //       //     <div>
    //       //       <div
    //       //         className={clsx(
    //       //           "flex items-center space-x-1.5 text-sm",
    //       //           delta.isDate2After ? "text-green-600" : "text-red-600"
    //       //         )}
    //       //       >
    //       //         <TriangleUpIcon
    //       //           className={clsx(
    //       //             "h-4 w-4",
    //       //             delta.isDate2After
    //       //               ? "text-green-500"
    //       //               : "rotate-180 transform text-red-500"
    //       //           )}
    //       //         />
    //       //         <div>{`${delta.absValue} ${delta.unit}`}</div>
    //       //         <TooltipProvider>
    //       //           <Tooltip delayDuration={0}>
    //       //             <TooltipTrigger>
    //       //               <CheckCircleIcon className="h-5 w-5 text-gray-500" />
    //       //             </TooltipTrigger>
    //       //             <TooltipContent side="right">Completed</TooltipContent>
    //       //           </Tooltip>
    //       //         </TooltipProvider>
    //       //       </div>

    //       //     </div>
    //       //   ) : (
    //       //     <div className="space-y-1">

    //       //     </div>
    //       //   )}
    //       // </>
    //     );
    //   },
    //   accessorFn: ({ assignedEndDate, journey }) => {
    //     if (!assignedEndDate || !journey || !settings) return 0;
    //     const forecastedEndDate = calculateEndDate(
    //       new Date(),
    //       journey.durations.forecasted,
    //       settings.workingTimeDetails,
    //       settings.timezone
    //     );
    //     const delta = getTimeDifference(
    //       forecastedEndDate,
    //       new Date(assignedEndDate)
    //     );
    //     return delta.absValue;
    //   },
    // },
    {
      id: "startDate",
      header: ({ column }) => {
        return (
          <div className="flex w-44 items-center space-x-1 truncate">
            <div>Start Date</div>
            {/* <SortToggle column={column} /> */}
            <NewSortToggle
              isActive={sort.property === "assignedStartDate"}
              order={sort.order}
              setSort={setSort}
              property={"assignedStartDate"}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        if (!row.original.assignedStartDate) return null;
        const startDate = new Date(row.original.assignedStartDate);
        return (
          <div className="h-full">
            {dayjs(startDate).format("MMM DD, YYYY")}
          </div>
        );
      },
      accessorFn: ({ assignedStartDate }) => {
        if (!assignedStartDate) return 0;
        return new Date(assignedStartDate).getTime();
      },
    },
    {
      id: "endDate",
      header: ({ column }) => {
        return (
          <div className="flex w-44 items-center space-x-1 truncate">
            <div>Target End Date</div>
            {/* <SortToggle column={column} /> */}
            <NewSortToggle
              isActive={sort.property === "assignedEndDate"}
              order={sort.order}
              setSort={setSort}
              property={"assignedEndDate"}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        if (!row.original.assignedEndDate) return null;
        const endDate = new Date(row.original.assignedEndDate);
        return (
          <div className="h-full">{dayjs(endDate).format("MMM DD, YYYY")}</div>
        );
      },
      accessorFn: ({ assignedEndDate }) => {
        if (!assignedEndDate) return 0;
        return new Date(assignedEndDate).getTime();
      },
    },
    {
      id: "forecastedCompletionDate",
      header: ({ column }) => {
        return (
          <div className="flex w-44 items-center space-x-1 truncate">
            <div>Forecasted End Date</div>
            {/* <SortToggle column={column} /> */}
            {/* <NewSortToggle
              isActive={sort.property === "forecastedEndDate"}
              order={sort.order}
              setSort={setSort}
              property={"forecastedEndDate"}
            /> */}
          </div>
        );
      },
      cell: ({ row }) => {
        if (!row.original.forecastedCompletionDate) return null;
        return (
          <div className="h-full">
            {dayjs(row.original.forecastedCompletionDate).format(
              "MMM DD, YYYY"
            )}
          </div>
        );
      },
      // accessorFn: ({ journey }) => {
      //   if (!journey || !settings) return 0;
      //   const forecastedEndDate = calculateEndDate(
      //     new Date(),
      //     journey.durations.forecasted,
      //     settings.workingTimeDetails,
      //     settings.timezone
      //   );
      //   return new Date(forecastedEndDate).getTime();
      // },
    },
    // {
    //   id: "forecastedCompletionDate",
    //   header: ({ column }) => {
    //     return (
    //       <div className="flex w-44 items-center space-x-1 truncate">
    //         <div>Forecasted End Date</div>
    //         {/* <SortToggle column={column} /> */}
    //         {/* <NewSortToggle
    //           isActive={sort.property === "forecastedEndDate"}
    //           order={sort.order}
    //           setSort={setSort}
    //           property={"forecastedEndDate"}
    //         /> */}
    //       </div>
    //     );
    //   },
    //   cell: ({ row }) => {
    //     if (!row.original.journey || !settings) return null;
    //     // if (row.original.type === "Simple") {
    //     //   return <div className="text-gray-500">-</div>;
    //     // }
    //     const forecastedEndDate = calculateEndDate(
    //       new Date(),
    //       row.original.journey.durations.forecasted,
    //       settings.workingTimeDetails,
    //       settings.timezone
    //     );
    //     return (
    //       <div className="h-full">
    //         <span>{dayjs(forecastedEndDate).format("MMM DD, YYYY")}</span>
    //         {/* <span className="text-sm text-gray-500">
    //           {dayjs(plannedCompletionDate).format("MMM DD, YYYY")}
    //         </span> */}
    //       </div>
    //     );
    //   },
    //   accessorFn: ({ journey }) => {
    //     if (!journey || !settings) return 0;
    //     const forecastedEndDate = calculateEndDate(
    //       new Date(),
    //       journey.durations.forecasted,
    //       settings.workingTimeDetails,
    //       settings.timezone
    //     );
    //     return new Date(forecastedEndDate).getTime();
    //   },
    // },
    {
      id: "template",
      header: ({ column }) => {
        return (
          <div className="flex w-44 items-center space-x-1">
            <div>Template</div>
            {/* <SortToggle column={column} /> */}
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="h-full w-44 truncate">
            {row.original.template?.name ?? ""}
          </div>
        );
      },
      accessorFn: ({ template }) => template?.name ?? "",
    },
  ];
  // add custom fields
  for (const key in columnsToDisplay) {
    const column = columnsToDisplay[key];
    if (column.isCustomField) {
      columns.push({
        id: column.id,
        header: () => {
          return (
            <div style={{ width: column.width }} className="truncate">
              {column.name}
            </div>
          );
        },
        cell: ({ row }) => {
          // find if the custom field exists in the row
          const customField = row.original.customFields?.find(
            (cf) => cf._id === column.id
          );
          if (customField) {
            if (customField.type === "date") {
              console.log(customField.value);
              if (!!customField.value) {
                return (
                  <div>{dayjs(customField.value).format("MMM DD, YYYY")}</div>
                );
              } else {
                return null;
              }
            } else if (customField.type === "multi-select") {
              const field = customFields.find(
                (cf) => cf._id === column.id
              ) as CustomFieldMultiSelectData;
              if (field) {
                return (
                  <div className="flex items-center space-x-1">
                    {customField.value?.map((v) => (
                      <div className="flex h-5 items-center rounded bg-gray-100 px-1.5 text-xs">
                        {field.options.find((o) => o.value === v)?.label}
                      </div>
                    ))}
                  </div>
                );
              }
            } else if (customField.type === "select") {
              const field = customFields.find(
                (cf) => cf._id === column.id
              ) as CustomFieldSelectData;
              if (field && !!customField.value) {
                return (
                  <div className="flex h-5 w-fit items-center rounded bg-gray-100 px-1.5 text-xs">
                    {
                      field.options.find((o) => o.value === customField.value)
                        ?.label
                    }
                  </div>
                );
              }
            } else if (customField.type === "currency") {
              if (customField.value) {
                return (
                  <div className="flex h-full items-center truncate text-sm">
                    <div className="text-gray-500">{"$"}</div>
                    <div>{customField.value?.toLocaleString()}</div>
                  </div>
                );
              }
            } else if (customField.type === "boolean") {
              if (customField.value) {
                return (
                  <div className="flex h-full items-center truncate text-sm">
                    <div className="text-gray-500">
                      {customField.value ? "Yes" : "No"}
                    </div>
                  </div>
                );
              }
            }
            return (
              <div className="h-full truncate">
                {customField.value?.toString()}
              </div>
            );
          }
        },

        enableSorting: false,
        enableHiding: false,
        // accessorFn: ({ customFields }) => {
        //   const customField = customFields?.find((cf) => cf._id === column.id);
        //   if (customField) {
        //     return customField.value;
        //   }
        // },
      });
    }
  }
  return columns.filter((column) => !!column);
}

export function NewSortToggle({
  isActive,
  order,
  setSort,
  property,
}: {
  isActive: boolean;
  order: "asc" | "desc";
  setSort: (sort: ProjectsSort) => void;
  property: ProjectsSort["property"];
}) {
  return (
    <button
      className="inline-flex rounded-md px-1 py-1 hover:bg-gray-300/20"
      onClick={() => {
        if (isActive && order === "asc") {
          setSort({ property, order: "desc" });
        } else {
          setSort({ property, order: "asc" });
        }
      }}
    >
      <ArrowUpIcon
        className={clsx(
          "h-3 w-3 stroke-2 text-gray-300",
          isActive && order === "asc" && "text-primary-main"
        )}
        // onClick={() => {
        //   column.toggleSorting(false);
        // }}
      />
      <ArrowDownIcon
        className={clsx(
          "h-3 w-3 stroke-2 text-gray-300",
          isActive && order === "desc" && "text-primary-main"
        )}
        // onClick={() => {
        //   // setSort({ property, order: "desc" });
        // }}
      />
      {/* <ChevronUpDownIcon className="h-4 w-4 text-gray-400" /> */}
    </button>
  );
}

// export function SortToggle({ column }: { column: Column<Project> }) {
//   return (
//     <button
//       className="inline-flex rounded-md px-1 py-1 hover:bg-gray-300/20"
//       onClick={() => {
//         if (column.getIsSorted() === "asc") {
//           column.toggleSorting(true);
//         } else {
//           column.toggleSorting(false);
//         }
//       }}
//     >
//       <ArrowUpIcon
//         className={clsx(
//           "h-3 w-3 stroke-2 text-gray-300",
//           column.getIsSorted() === "asc" && "text-primary-main"
//         )}
//         // onClick={() => {
//         //   column.toggleSorting(false);
//         // }}
//       />
//       <ArrowDownIcon
//         className={clsx(
//           "h-3 w-3 stroke-2 text-gray-300",
//           column.getIsSorted() === "desc" && "text-primary-main"
//         )}
//         onClick={() => {
//           column.toggleSorting(true);
//         }}
//       />
//       {/* <ChevronUpDownIcon className="h-4 w-4 text-gray-400" /> */}
//     </button>
//   );
// }

function InternalUpdateSymbol({
  update,
  index,
}: {
  update: InternalPhaseUpdateData;
  index: number;
}) {
  // if (!!update.isSkipped) {
  //   return (
  //     <div className="flex h-3 w-3 items-center justify-center rounded-full border border-gray-500 bg-gray-100">
  //       <XMarkIcon strokeWidth={2.5} className="h-3 w-3 text-gray-500" />
  //     </div>
  //   );
  // }
  switch (update.status) {
    case "On-Track":
      return (
        <div
          className={clsx(
            "rounded-full border border-green-500 bg-green-200",
            index === 0 ? "h-3 w-3 border" : "h-2 w-2 border-opacity-80"
          )}
        ></div>
      );
    case "Off-Track":
      return (
        <div
          className={clsx(
            "rounded-full border border-red-500 bg-red-200",
            index === 0 ? "h-3 w-3 border" : "h-2 w-2 border-opacity-80"
          )}
        ></div>
      );
    case "At Risk":
      return (
        <div
          className={clsx(
            "rounded-full border border-yellow-500 bg-yellow-200",
            index === 0 ? "h-3 w-3 border" : "h-2 w-2 border-opacity-80"
          )}
        ></div>
      );
    default:
      return null;
  }
}

const CellSkeleton = forwardRef<HTMLDivElement, { className?: string }>(
  ({ className }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(`h-3 animate-pulse rounded bg-gray-100`, className)}
      ></div>
    );
  }
);
CellSkeleton.displayName = "CellSkeleton";

function transformColumnsToDisplay(columnsToDisplay: ColumnsToDisplay): {
  [key: string]: boolean;
} {
  const transformedColumnsToDisplay: { [key: string]: boolean } = {};
  for (const columnId in columnsToDisplay) {
    transformedColumnsToDisplay[columnId] =
      !!columnsToDisplay[columnId].visible;
  }
  return transformedColumnsToDisplay;
}

// export function GeneralProjectsListNew({
//   projects,
//   // filterGroup,
//   columnsToDisplay,
//   projectDetails,
//   search,
//   setSearch,
//   // setNoOfProjectsAfterSearch,
//   setColumnsToDisplay,
// }: {
//   projects: ProjectDataV2[];
//   // filterGroup: ProjectsV2FilterGroup;
//   columnsToDisplay: ColumnsToDisplay;
//   projectDetails: ProjectDetailsData[] | undefined;
//   search: string;
//   setSearch: (search: string) => void;
//   // setNoOfProjectsAfterSearch: React.Dispatch<React.SetStateAction<number>>;
//   setColumnsToDisplay: React.Dispatch<React.SetStateAction<ColumnsToDisplay>>;
// }) {
//   // console.log(columnsToDisplay);
//   const { data: customFields } = useGetCustomFields("project");
//   const [sorting, setSorting] = useState<SortingState>([]);
//   const data = useMemo<Project[]>(() => {
//     if (!projects) return [];
//     // return projects;
//     return projects.map((project) => ({
//       ...project,
//       ...(projectDetails?.find(
//         (projectDetails) => project._id === projectDetails._id
//       ) || {}),
//     }));
//   }, [projects, projectDetails]);
//   const { data: settings } = useGetSettings();
//   const columns = useMemo(
//     () =>
//       getColumns(columnsToDisplay, customFields ?? [], settings, sort, setSort),
//     [customFields, columnsToDisplay, settings, sort, setSort]
//   );

//   const table = useReactTable<Project>({
//     data,
//     columns,
//     getCoreRowModel: getCoreRowModel(),
//     state: {
//       sorting,
//       globalFilter: search,
//       columnVisibility: transformColumnsToDisplay(columnsToDisplay),
//     },
//     globalFilterFn: fuzzyFilter,
//     onSortingChange: setSorting,
//     onGlobalFilterChange: setSearch,
//     getSortedRowModel: getSortedRowModel(),
//     getFilteredRowModel: getFilteredRowModel(),
//     // onColumnVisibilityChange: setColumnsToDisplay,
//   });
//   const navigate = useNavigate();

//   // useEffect(() => {
//   //   setNoOfProjectsAfterSearch(table.getRowModel().rows.length);
//   // }, [table.getRowModel().rows]);

//   const { rows } = table.getRowModel();

//   const visibleColumns = table.getVisibleLeafColumns();

//   //The virtualizers need to know the scrollable container element
//   const tableContainerRef = useRef<HTMLDivElement>(null);

//   //we are using a slightly different virtualization strategy for columns (compared to virtual rows) in order to support dynamic row heights
//   const columnVirtualizer = useVirtualizer({
//     count: visibleColumns.length,
//     estimateSize: (index) => visibleColumns[index].getSize(), //estimate width of each column for accurate scrollbar dragging
//     getScrollElement: () => tableContainerRef.current,
//     horizontal: true,
//     overscan: 3, //how many columns to render on each side off screen each way (adjust this for performance)
//   });

//   //dynamic row height virtualization - alternatively you could use a simpler fixed row height strategy without the need for `measureElement`
//   const rowVirtualizer = useVirtualizer({
//     count: rows.length,
//     estimateSize: () => 24, //estimate row height for accurate scrollbar dragging
//     getScrollElement: () => tableContainerRef.current,
//     //measure dynamic row height, except in firefox because it measures table border height incorrectly
//     measureElement:
//       typeof window !== "undefined" &&
//       navigator.userAgent.indexOf("Firefox") === -1
//         ? (element) => element?.getBoundingClientRect().height
//         : undefined,
//     overscan: 5,
//   });

//   const virtualColumns = columnVirtualizer.getVirtualItems();
//   const virtualRows = rowVirtualizer.getVirtualItems();

//   //different virtualization strategy for columns - instead of absolute and translateY, we add empty columns to the left and right
//   let virtualPaddingLeft: number | undefined;
//   let virtualPaddingRight: number | undefined;

//   if (columnVirtualizer && virtualColumns?.length) {
//     virtualPaddingLeft = virtualColumns[0]?.start ?? 0;
//     virtualPaddingRight =
//       columnVirtualizer.getTotalSize() -
//       (virtualColumns[virtualColumns.length - 1]?.end ?? 0);
//   }

//   if (table.getRowModel().rows.length === 0) {
//     return <NoProjectsMeetFilter />;
//   }

//   return (
//     <div
//       className="container"
//       ref={tableContainerRef}
//       style={{
//         overflow: "auto", //our scrollable table container
//         position: "relative", //needed for sticky header
//         height: "800px", //should be a fixed height
//       }}
//     >
//       {/* Even though we're still using sematic table tags, we must use CSS grid and flexbox for dynamic row heights */}
//       <table style={{ display: "grid" }}>
//         <thead
//           style={{
//             display: "grid",
//             position: "sticky",
//             top: 0,
//             zIndex: 1,
//           }}
//         >
//           {table.getHeaderGroups().map((headerGroup) => (
//             <tr key={headerGroup.id} style={{ display: "flex", width: "100%" }}>
//               {virtualPaddingLeft ? (
//                 //fake empty column to the left for virtualization scroll padding
//                 <th style={{ display: "flex", width: virtualPaddingLeft }} />
//               ) : null}
//               {virtualColumns.map((vc) => {
//                 const header = headerGroup.headers[vc.index];
//                 return (
//                   <th
//                     key={header.id}
//                     style={{
//                       display: "flex",
//                       width: header.getSize(),
//                     }}
//                   >
//                     <div
//                       {...{
//                         className: header.column.getCanSort()
//                           ? "cursor-pointer select-none"
//                           : "",
//                         onClick: header.column.getToggleSortingHandler(),
//                       }}
//                     >
//                       {flexRender(
//                         header.column.columnDef.header,
//                         header.getContext()
//                       )}
//                       {{
//                         asc: " 🔼",
//                         desc: " 🔽",
//                       }[header.column.getIsSorted() as string] ?? null}
//                     </div>
//                   </th>
//                 );
//               })}
//               {virtualPaddingRight ? (
//                 //fake empty column to the right for virtualization scroll padding
//                 <th style={{ display: "flex", width: virtualPaddingRight }} />
//               ) : null}
//             </tr>
//           ))}
//         </thead>
//         <tbody
//           style={{
//             display: "grid",
//             height: `${rowVirtualizer.getTotalSize()}px`, //tells scrollbar how big the table is
//             position: "relative", //needed for absolute positioning of rows
//           }}
//         >
//           {virtualRows.map((virtualRow) => {
//             const row = rows[virtualRow.index] as Row<Project>;
//             const visibleCells = row.getVisibleCells();

//             return (
//               <tr
//                 data-index={virtualRow.index} //needed for dynamic row height measurement
//                 ref={(node) => rowVirtualizer.measureElement(node)} //measure dynamic row height
//                 key={row.id}
//                 style={{
//                   display: "flex",
//                   position: "absolute",
//                   transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
//                   width: "100%",
//                 }}
//               >
//                 {virtualPaddingLeft ? (
//                   //fake empty column to the left for virtualization scroll padding
//                   <td style={{ display: "flex", width: virtualPaddingLeft }} />
//                 ) : null}
//                 {virtualColumns.map((vc) => {
//                   const cell = visibleCells[vc.index];
//                   return (
//                     <td
//                       key={cell.id}
//                       style={{
//                         display: "flex",
//                         width: cell.column.getSize(),
//                       }}
//                     >
//                       {flexRender(
//                         cell.column.columnDef.cell,
//                         cell.getContext()
//                       )}
//                     </td>
//                   );
//                 })}
//                 {virtualPaddingRight ? (
//                   //fake empty column to the right for virtualization scroll padding
//                   <td style={{ display: "flex", width: virtualPaddingRight }} />
//                 ) : null}
//               </tr>
//             );
//           })}
//         </tbody>
//       </table>
//     </div>
//   );

//   // return (
//   //   <div className="flex-1 overflow-auto">
//   //     <div className="max-h-full max-w-full overflow-auto">
//   //       <table
//   //         style={{ borderCollapse: "separate", borderSpacing: 0 }}
//   //         className="min-w-[600px]"
//   //       >
//   //         <thead className="overflow-auto">
//   //           {table.getHeaderGroups().map((headerGroup) => (
//   //             <tr key={headerGroup.id} className="border-b">
//   //               {headerGroup.headers.map((header) => (
//   //                 <th
//   //                   key={header.id}
//   //                   scope="col"
//   //                   className={
//   //                     "sticky top-0 z-10 h-11 whitespace-nowrap border-b border-r border-gray-200/70 bg-gray-50 px-6 text-left text-xs font-normal uppercase tracking-wide text-gray-400 first:left-0 first:z-20 first:w-full first:pl-8"
//   //                   }
//   //                 >
//   //                   {header.isPlaceholder
//   //                     ? null
//   //                     : flexRender(
//   //                         header.column.columnDef.header,
//   //                         header.getContext()
//   //                       )}
//   //                 </th>
//   //               ))}
//   //             </tr>
//   //           ))}
//   //         </thead>
//   //         <tbody className="divide-y divide-gray-200 bg-white">
//   //           {!projects ? (
//   //             <tr>
//   //               {/* <td className="inset-0 flex items-center justify-center p-4 pl-8"> */}
//   //               <div className="sticky left-0 z-10 p-4 pl-8 text-gray-400">
//   //                 Loading...
//   //               </div>
//   //               {/* </td> */}
//   //             </tr>
//   //           ) : (
//   //             <>
//   //               {projects.length === 0 ? (
//   //                 <tr>
//   //                   <div className="sticky left-0 z-10 whitespace-nowrap p-4 pl-8 text-gray-400">
//   //                     No projects
//   //                   </div>
//   //                   {/* <td className="inset-0 flex items-center justify-center p-4">
//   //               </td> */}
//   //                 </tr>
//   //               ) : (
//   //                 <>
//   //                   {table.getRowModel().rows.map((row) => (
//   //                     <tr
//   //                       key={row.id}
//   //                       className="group relative rounded-lg hover:cursor-pointer hover:bg-gray-50"
//   //                       onClick={() => {
//   //                         navigate(`/projects/${row.original._id}/home`);
//   //                       }}
//   //                     >
//   //                       {row.getVisibleCells().map((cell) => (
//   //                         <td
//   //                           key={cell.id}
//   //                           className={
//   //                             "whitespace-nowrap border-r border-b border-gray-100 px-6 py-3 text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:bg-white first:pr-6 first:font-medium first:text-gray-900 first:group-hover:bg-gray-50 first:sm:pl-8"
//   //                           }
//   //                         >
//   //                           {flexRender(
//   //                             cell.column.columnDef.cell,
//   //                             cell.getContext()
//   //                           )}
//   //                         </td>
//   //                       ))}
//   //                     </tr>
//   //                   ))}
//   //                 </>
//   //               )}
//   //             </>
//   //           )}
//   //         </tbody>
//   //       </table>
//   //     </div>
//   //   </div>
//   // );
// }

function ProgressIcon() {
  return (
    <div className="flex w-4 justify-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        className="h-3.5 w-3.5"
      >
        <line x1="19" x2="5" y1="5" y2="19" />
        <circle cx="6.5" cy="6.5" r="2.5" />
        <circle cx="17.5" cy="17.5" r="2.5" />
      </svg>
    </div>
  );
}
